import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, Plus, Copy, Archive, Users, Download, Ticket, Store, Pencil, Trash2, Save, ArchiveRestore, CalendarPlus, UtensilsCrossed, X, Check, Sparkles, Link, UserPlus, PoundSterling, ShoppingBag, ScanLine, FileDown, FileText } from "lucide-react";
import wordIcon from "@/assets/word-icon.png";
import pdfIcon from "@/assets/pdf-icon.png";
import csvIcon from "@/assets/csv-icon.png";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePtaAccess } from "@/hooks/usePtaAccess";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface CateringAssignment {
  id: string;
  quantity: number;
  notes: string | null;
  stock_item?: { item_name: string } | null;
  menu_item?: { name: string; icon: string } | null;
}

export default function EventsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasAddon, isStandardPlan } = usePtaAccess();
  const showExternalStalls = hasAddon("Community Dashboard");
  const showCatering = hasAddon("Catering Dashboard");
  const [events, setEvents] = useState<any[]>([]);
  const [stallCounts, setStallCounts] = useState<Record<string, number>>({});
  const [cateringCounts, setCateringCounts] = useState<Record<string, number>>({});
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [stallApplications, setStallApplications] = useState<any[]>([]);
  const [applicationsDialogOpen, setApplicationsDialogOpen] = useState(false);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [createFromTemplateOpen, setCreateFromTemplateOpen] = useState(false);
  const [addStallDialogOpen, setAddStallDialogOpen] = useState(false);
  const [attendeeDialogOpen, setAttendeeDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [assignStallDialogOpen, setAssignStallDialogOpen] = useState(false);
  const [selectedStall, setSelectedStall] = useState<any>(null);
  const [assignStallToEventDialogOpen, setAssignStallToEventDialogOpen] = useState(false);
  const [selectedEventForStallAssign, setSelectedEventForStallAssign] = useState<any>(null);
  const [selectedStallIds, setSelectedStallIds] = useState<string[]>([]);
  const [stallAssignments, setStallAssignments] = useState<Record<string, any[]>>({});
  const [cateringDialogOpen, setCateringDialogOpen] = useState(false);
  const [eventCatering, setEventCatering] = useState<CateringAssignment[]>([]);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [eventToSaveAsTemplate, setEventToSaveAsTemplate] = useState<any>(null);
  const [selectedUtilities, setSelectedUtilities] = useState<{name: string, qty: number}[]>([]);
  const [customUtilityInput, setCustomUtilityInput] = useState("");
  const [availableUtilities, setAvailableUtilities] = useState<string[]>([
    "Power", "Water", "Gas", "WiFi", "Tables", "Chairs", "Tent/Gazebo", "Lighting", "Extension Leads", "Card Reader"
  ]);
  const [reusableStalls, setReusableStalls] = useState<any[]>([]);
  const [editStallDialogOpen, setEditStallDialogOpen] = useState(false);
  const [editingStall, setEditingStall] = useState<any>(null);
  const [editStallUtilities, setEditStallUtilities] = useState<{name: string, qty: number}[]>([]);
  const [priceTiers, setPriceTiers] = useState<{description: string, price: number}[]>([]);
  const [editPriceTiers, setEditPriceTiers] = useState<{description: string, price: number}[]>([]);
  const [eventPlannerDialogOpen, setEventPlannerDialogOpen] = useState(false);
  const [selectedEventForPlan, setSelectedEventForPlan] = useState<any>(null);
  const [selectedPlannerEvent, setSelectedPlannerEvent] = useState<string>("");
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [generatedPlanData, setGeneratedPlanData] = useState<{
    event: any;
    planText: string;
    stats: { volunteers: number; floats: number; stalls: number; catering: number };
  } | null>(null);
  const [selectedHelperEvent, setSelectedHelperEvent] = useState<string>("");
  const [eventHelpers, setEventHelpers] = useState<any[]>([]);
  const [editHelperDialogOpen, setEditHelperDialogOpen] = useState(false);
  const [editingHelper, setEditingHelper] = useState<any>(null);
  const [addHelperDialogOpen, setAddHelperDialogOpen] = useState(false);
  const [ptaId, setPtaId] = useState<string>("");
  const [ptaInfo, setPtaInfo] = useState<{ name: string; logo_url: string | null } | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [ticketPriceTiers, setTicketPriceTiers] = useState<any[]>([]);
  const [ticketExtras, setTicketExtras] = useState<any[]>([]);
  const [selectedTicketEvent, setSelectedTicketEvent] = useState<string>("");
  const [ticketDialogOpenId, setTicketDialogOpenId] = useState<string | null>(null);
  useEffect(() => {
    if (user?.id) {
      fetchEvents();
      fetchTemplates();
      fetchCateringCounts();
    }
  }, [user, showArchived]);

  const fetchEvents = async () => {
    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;
    
    setPtaId(memberData.pta_id);

    // Fetch PTA info for branding
    const { data: ptaData } = await supabase
      .from('ptas')
      .select('name, logo_url')
      .eq('id', memberData.pta_id)
      .maybeSingle();
    if (ptaData) setPtaInfo(ptaData);

    // Fetch members for this PTA
    const { data: membersData } = await supabase
      .from('members')
      .select('id, full_name, email')
      .eq('pta_id', memberData.pta_id)
      .eq('status', 'active');
    
    if (membersData) {
      setMembers(membersData);
    }

    const { data: eventsData, error } = await supabase
      .from('events')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .eq('is_archived', showArchived)
      .order('event_date', { ascending: true });

    if (!error && eventsData) {
      setEvents(eventsData);
      
      // Fetch stall application counts for each event (filtered by PTA)
      const { data: stallData } = await supabase
        .from('stall_applications')
        .select('event_id, status')
        .eq('pta_id', memberData.pta_id);

      if (stallData) {
        const counts: Record<string, number> = {};
        stallData.forEach(app => {
          if (!counts[app.event_id]) {
            counts[app.event_id] = 0;
          }
          counts[app.event_id]++;
        });
        setStallCounts(counts);
      }
    }
  };

  const fetchEventHelpers = async (eventId: string) => {
    const { data, error } = await supabase
      .from('event_helpers')
      .select(`
        id,
        member_id,
        volunteer_name,
        volunteer_email,
        volunteer_phone,
        role_task,
        created_at,
        members (id, full_name, email)
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(error);
      toast.error("Failed to load helpers");
    } else {
      setEventHelpers(data || []);
    }
  };

  const fetchTemplates = async () => {
    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { data, error } = await supabase
      .from('event_templates')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setTemplates(data);
    }
  };

  const fetchCateringCounts = async () => {
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { data: cateringData } = await supabase
      .from('event_catering_assignments')
      .select('event_id')
      .eq('pta_id', memberData.pta_id);

    if (cateringData) {
      const counts: Record<string, number> = {};
      cateringData.forEach((item: { event_id: string }) => {
        if (!counts[item.event_id]) {
          counts[item.event_id] = 0;
        }
        counts[item.event_id]++;
      });
      setCateringCounts(counts);
    }
  };

  const handleViewCatering = async (event: any) => {
    setSelectedEvent(event);
    setCateringDialogOpen(true);
    
    const { data, error } = await supabase
      .from('event_catering_assignments')
      .select(`
        id,
        quantity,
        notes,
        catering_stock (item_name),
        catering_menu_items (name, icon)
      `)
      .eq('event_id', event.id);

    if (error) {
      toast.error("Failed to load catering assignments");
      console.error(error);
    } else {
      // Transform the data to match the interface
      const transformed = (data || []).map((item: any) => ({
        id: item.id,
        quantity: item.quantity,
        notes: item.notes,
        stock_item: item.catering_stock,
        menu_item: item.catering_menu_items
      }));
      setEventCatering(transformed);
    }
  };

  const handleViewApplications = async (event: any) => {
    setSelectedEvent(event);
    setApplicationsDialogOpen(true);
    
    const { data, error } = await supabase
      .from('stall_applications')
      .select('*')
      .eq('event_id', event.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error("Failed to load applications");
      console.error(error);
    } else {
      setStallApplications(data || []);
    }
  };

  const handleEditEvent = (event: any) => {
    setEditingEvent(event);
    setEditEventDialogOpen(true);
  };

  const handleDuplicateEvent = async (event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const { id, created_at, updated_at, is_archived, ...eventData } = event;
    const { error } = await supabase
      .from('events')
      .insert({
        ...eventData,
        name: `${event.name} (Copy)`,
      });

    if (error) {
      toast.error("Failed to duplicate event");
      console.error(error);
    } else {
      toast.success("Event duplicated successfully");
      fetchEvents();
    }
  };

  const handleDeleteEvent = async (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this event?")) return;

    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId);

    if (error) {
      toast.error("Failed to delete event");
      console.error(error);
    } else {
      toast.success("Event deleted successfully");
      fetchEvents();
    }
  };

  const openSaveTemplateDialog = (event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEventToSaveAsTemplate(event);
    setSaveTemplateDialogOpen(true);
  };

  const handleSaveAsTemplate = async (formData: FormData) => {
    if (!eventToSaveAsTemplate) return;
    
    const templateDescription = formData.get('template-description') as string;
    
    // Only insert valid template columns
    const { data: templateData, error: templateError } = await supabase
      .from('event_templates')
      .insert({
        name: eventToSaveAsTemplate.name,
        description: templateDescription,
        location: eventToSaveAsTemplate.location,
        pta_id: eventToSaveAsTemplate.pta_id,
        created_by: user?.id,
      })
      .select()
      .single();

    if (templateError) {
      toast.error("Failed to save template");
      console.error(templateError);
      return;
    }

    // Also save stall applications associated with this event
    const { data: stallApps } = await supabase
      .from('stall_applications')
      .select('business_name, contact_name, contact_email, contact_phone, description')
      .eq('event_id', eventToSaveAsTemplate.id);

    if (stallApps && stallApps.length > 0) {
      const templateStalls = stallApps.map(stall => ({
        template_id: templateData.id,
        pta_id: eventToSaveAsTemplate.pta_id,
        business_name: stall.business_name,
        contact_name: stall.contact_name,
        contact_email: stall.contact_email,
        contact_phone: stall.contact_phone,
        description: stall.description,
      }));

      const { error: stallsError } = await supabase
        .from('event_template_stalls')
        .insert(templateStalls);

      if (stallsError) {
        console.error("Failed to save template stalls:", stallsError);
      }
    }

    toast.success(`Template saved with ${stallApps?.length || 0} stalls`);
    setSaveTemplateDialogOpen(false);
    setEventToSaveAsTemplate(null);
    fetchTemplates();
  };

  const handleArchiveEvent = async (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const { error } = await supabase
      .from('events')
      .update({ is_archived: true })
      .eq('id', eventId);

    if (error) {
      toast.error("Failed to archive event");
      console.error(error);
    } else {
      toast.success("Event archived successfully");
      fetchEvents();
    }
  };

  const handleRestoreEvent = async (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const { error } = await supabase
      .from('events')
      .update({ is_archived: false })
      .eq('id', eventId);

    if (error) {
      toast.error("Failed to restore event");
      console.error(error);
    } else {
      toast.success("Event restored successfully");
      fetchEvents();
    }
  };

  const handleCreateFromTemplate = async (formData: FormData) => {
    if (!selectedTemplate) return;

    const eventName = formData.get('event-name') as string;
    const eventDate = formData.get('event-date') as string;
    const eventTime = formData.get('event-time') as string;

    // Create the event
    const { data: newEvent, error: eventError } = await supabase
      .from('events')
      .insert({
        name: eventName,
        description: selectedTemplate.description,
        location: selectedTemplate.location,
        pta_id: selectedTemplate.pta_id,
        event_date: eventDate,
        time: eventTime,
        status: 'active',
      })
      .select()
      .single();

    if (eventError) {
      toast.error("Failed to create event from template");
      console.error(eventError);
      return;
    }

    // Also create stall applications from template stalls
    const { data: templateStalls } = await supabase
      .from('event_template_stalls')
      .select('*')
      .eq('template_id', selectedTemplate.id);

    if (templateStalls && templateStalls.length > 0) {
      const stallApplications = templateStalls.map(stall => ({
        event_id: newEvent.id,
        pta_id: selectedTemplate.pta_id,
        business_name: stall.business_name,
        contact_name: stall.contact_name || '',
        contact_email: stall.contact_email || '',
        contact_phone: stall.contact_phone || '',
        description: stall.description || '',
        status: 'approved',
        payment_amount: 0,
      }));

      const { error: stallsError } = await supabase
        .from('stall_applications')
        .insert(stallApplications);

      if (stallsError) {
        console.error("Failed to create stall applications:", stallsError);
      }
    }

    toast.success(`Event created with ${templateStalls?.length || 0} stalls`);
    setCreateFromTemplateOpen(false);
    setSelectedTemplate(null);
    fetchEvents();
  };

  const handleGenerateEventPlan = async (event: any) => {
    setIsGeneratingPlan(true);
    setGeneratedPlanData(null);
    
    try {
      // Fetch all related data
      const assignedStalls = stallAssignments[event.id] || [];
      
      // Fetch external stall applications (all statuses)
      const { data: externalStalls } = await supabase
        .from('stall_applications')
        .select('business_name, contact_name, payment_status, status')
        .eq('event_id', event.id);

      // Fetch catering assignments
      const { data: cateringData } = await supabase
        .from('event_catering_assignments')
        .select(`
          quantity,
          notes,
          catering_stock (item_name),
          catering_menu_items (name),
          catering_stock_items (item_name)
        `)
        .eq('event_id', event.id);

      // Fetch event helpers with a role/task assigned
      const { data: helpersData } = await supabase
        .from('event_helpers')
        .select('volunteer_name, role_task, members (full_name)')
        .eq('event_id', event.id)
        .not('role_task', 'is', null);

      // Calculate totals
      let totalVolunteers = 0;
      let totalFloats = 0;
      const equipmentSummary: Record<string, number> = {};

      assignedStalls.forEach((stall: any) => {
        totalVolunteers += stall.volunteers_required || 0;
        totalFloats += stall.floats_needed || 0;
        (stall.utilities || []).forEach((util: string) => {
          const match = util.match(/^(.+?)\s*\((\d+)\)$/);
          const name = match ? match[1] : util;
          const qty = match ? parseInt(match[2]) : 1;
          equipmentSummary[name] = (equipmentSummary[name] || 0) + qty;
        });
      });

      // Format catering items
      const cateringItems = cateringData?.map((item: any) => ({
        name: item.catering_stock?.item_name || item.catering_menu_items?.name || item.catering_stock_items?.item_name || 'Unknown Item',
        quantity: item.quantity,
        notes: item.notes
      })) || [];

      // Format helpers
      const registeredHelpers = (helpersData || [])
        .filter((h: any) => h.role_task && h.role_task.trim() !== '')
        .map((h: any) => ({
          name: h.volunteer_name || h.members?.full_name || 'Unknown',
          role: h.role_task
        }));

      // Format vendors simply: BusinessName (ContactName): PaymentStatus
      const vendorList = (externalStalls || []).map((s: any) => ({
        display: `${s.business_name} (${s.contact_name || 'No contact'}): ${s.payment_status || 'N/A'}`,
      }));

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('generate-event-plan', {
        body: {
          eventData: {
            eventName: event.name,
            eventDate: new Date(event.event_date).toLocaleDateString(),
            eventTime: event.time,
            location: event.location,
            description: event.description,
            ptaStalls: assignedStalls,
            externalStalls: vendorList,
            registeredHelpers,
            cateringItems,
            totalVolunteers,
            totalFloats,
            equipmentSummary
          }
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        toast.error('Failed to generate plan. Please try again.');
        setIsGeneratingPlan(false);
        return;
      }

      if (data.error) {
        toast.error(data.error);
        setIsGeneratingPlan(false);
        return;
      }

      // Store the generated plan data
      setGeneratedPlanData({
        event,
        planText: data.plan || 'No plan generated',
        stats: {
          volunteers: totalVolunteers,
          floats: totalFloats,
          stalls: assignedStalls.length,
          catering: cateringItems.length
        }
      });
      
      toast.success('Plan generated! Download as PDF or Word.');
    } catch (err) {
      console.error('Error generating plan:', err);
      toast.error('Failed to generate plan. Please try again.');
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const downloadPlanAsPDF = () => {
    if (!generatedPlanData) return;
    
    const { event, planText, stats } = generatedPlanData;
    const doc = new jsPDF();
    const eventDate = new Date(event.event_date).toLocaleDateString();
    const fileName = `${event.name.replace(/\s+/g, '-')}-Event-Plan-${eventDate}.pdf`;

    let yPos = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);

    // Header - blue text, no background
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text(event.name, margin, 18);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`${eventDate} • ${event.time || 'Time TBD'} • ${event.location || 'Location TBD'}`, margin, 28);
    
    doc.setTextColor(0, 0, 0);
    yPos = 38;

    // Quick stats - no background
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Volunteers: ${stats.volunteers}`, margin + 8, yPos + 8);
    doc.text(`Floats: ${stats.floats}`, margin + 58, yPos + 8);
    doc.text(`Stalls: ${stats.stalls}`, margin + 98, yPos + 8);
    doc.text(`Catering: ${stats.catering} items`, margin + 138, yPos + 8);
    yPos += 20;

    // Process AI content
    const planLines = planText.split('\n');
    doc.setFont('helvetica', 'normal');
    
    planLines.forEach((line: string) => {
      if (yPos > 275) {
        doc.addPage();
        yPos = 25;
      }
      
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        yPos += 3;
        return;
      }
      
      if (trimmedLine.startsWith('##')) {
        yPos += 5;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(59, 130, 246);
        const headerText = trimmedLine.replace(/^#+\s*/, '');
        doc.text(headerText, margin, yPos + 2);
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos + 5, margin + maxWidth, yPos + 5);
        doc.setTextColor(0, 0, 0);
        yPos += 12;
      } else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•') || trimmedLine.startsWith('*')) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const bulletText = trimmedLine.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '');
        const wrappedLines = doc.splitTextToSize(`• ${bulletText}`, maxWidth - 10);
        wrappedLines.forEach((wLine: string, idx: number) => {
          if (yPos > 275) {
            doc.addPage();
            yPos = 25;
          }
          doc.text(idx === 0 ? wLine : `  ${wLine}`, margin + 5, yPos);
          yPos += 5;
        });
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const cleanText = trimmedLine.replace(/\*\*/g, '');
        const wrappedLines = doc.splitTextToSize(cleanText, maxWidth);
        wrappedLines.forEach((wLine: string) => {
          if (yPos > 275) {
            doc.addPage();
            yPos = 25;
          }
          doc.text(wLine, margin, yPos);
          yPos += 5;
        });
      }
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`PTA Hub Event Plan • Page ${i}/${pageCount}`, margin, 290);
      doc.text(new Date().toLocaleDateString(), pageWidth - margin - 25, 290);
    }

    doc.save(fileName);
    toast.success('PDF downloaded!');
  };

  const downloadPlanAsWord = () => {
    if (!generatedPlanData) return;
    
    const { event, planText, stats } = generatedPlanData;
    const eventDate = new Date(event.event_date).toLocaleDateString();
    const fileName = `${event.name.replace(/\s+/g, '-')}-Event-Plan-${eventDate}.doc`;

    // Create HTML content for Word
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
  h1 { color: #3b82f6; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
  h2 { color: #1e40af; margin-top: 20px; }
  .meta { color: #666; margin-bottom: 20px; }
  .stats { padding: 15px 0; margin-bottom: 20px; }
  .stats span { margin-right: 20px; font-weight: bold; }
  ul { margin: 10px 0; padding-left: 20px; }
  li { margin-bottom: 5px; }
</style>
</head>
<body>
<h1>${event.name}</h1>
<p class="meta">${eventDate} • ${event.time || 'Time TBD'} • ${event.location || 'Location TBD'}</p>
<div class="stats">
  <span>Volunteers: ${stats.volunteers}</span>
  <span>Floats: ${stats.floats}</span>
  <span>Stalls: ${stats.stalls}</span>
  <span>Catering: ${stats.catering} items</span>
</div>
${planText
  .split('\n')
  .map(line => {
    const trimmed = line.trim();
    if (!trimmed) return '<br>';
    if (trimmed.startsWith('##')) return `<h2>${trimmed.replace(/^#+\s*/, '')}</h2>`;
    if (trimmed.startsWith('-') || trimmed.startsWith('•') || trimmed.startsWith('*')) {
      return `<li>${trimmed.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '')}</li>`;
    }
    return `<p>${trimmed.replace(/\*\*/g, '')}</p>`;
  })
  .join('\n')}
<hr>
<p style="color: #999; font-size: 12px;">PTA Hub Event Plan • Generated ${new Date().toLocaleDateString()}</p>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Word document downloaded!');
  };

  const downloadEventSummaryPDF = async (event: any) => {
    try {
      toast.info('Generating summary...');
      
      const assignedStalls = stallAssignments[event.id] || [];
      
      // Fetch external vendor applications
      const { data: externalStalls } = await supabase
        .from('stall_applications')
        .select('business_name, status')
        .eq('event_id', event.id);

      // Fetch catering assignments
      const { data: cateringData } = await supabase
        .from('event_catering_assignments')
        .select(`
          quantity,
          notes,
          catering_stock (item_name),
          catering_menu_items (name),
          catering_stock_items (item_name)
        `)
        .eq('event_id', event.id);

      // Fetch event helpers/volunteers
      const { data: helpersData } = await supabase
        .from('event_helpers')
        .select('volunteer_name, role_task, members (full_name)')
        .eq('event_id', event.id);

      // Fetch cash floats assigned to this event
      const { data: floatsData } = await supabase
        .from('cash_floats')
        .select('float_name, amount, status')
        .eq('event_id', event.id);

      // Calculate totals
      let totalVolunteers = 0;
      let totalFloatsNeeded = 0;
      const equipmentSummary: Record<string, number> = {};

      assignedStalls.forEach((stall: any) => {
        totalVolunteers += stall.volunteers_required || 0;
        totalFloatsNeeded += stall.floats_needed || 0;
        (stall.utilities || []).forEach((util: string) => {
          const match = util.match(/^(.+?)\s*\((\d+)\)$/);
          const name = match ? match[1] : util;
          const qty = match ? parseInt(match[2]) : 1;
          equipmentSummary[name] = (equipmentSummary[name] || 0) + qty;
        });
      });

      const cateringItems = cateringData?.map((item: any) => ({
        name: item.catering_stock?.item_name || item.catering_menu_items?.name || item.catering_stock_items?.item_name || 'Unknown Item',
        quantity: item.quantity,
        notes: item.notes
      })) || [];

      const helpers = helpersData?.map((h: any) => ({
        name: h.volunteer_name || h.members?.full_name || 'Unknown',
        role: h.role_task || 'General Helper'
      })) || [];

      const floats = floatsData || [];

      // Build PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - margin * 2;
      let yPos = 0;

      const checkPage = (needed: number) => {
        if (yPos + needed > 275) {
          doc.addPage();
          yPos = 25;
        }
      };

      const drawSectionHeader = (title: string) => {
        checkPage(18);
        yPos += 4;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(59, 130, 246);
        doc.text(title, margin, yPos + 2);
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos + 5, margin + maxWidth, yPos + 5);
        doc.setTextColor(0, 0, 0);
        yPos += 12;
      };

      const drawBullet = (text: string, indent = 0) => {
        checkPage(7);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(`• ${text}`, maxWidth - 10 - indent);
        lines.forEach((line: string, idx: number) => {
          checkPage(6);
          doc.text(idx === 0 ? line : `  ${line}`, margin + 5 + indent, yPos);
          yPos += 5;
        });
      };

      // Header - event name + date/location (no background)
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text(event.name, margin, 18);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      const eventDate = new Date(event.event_date).toLocaleDateString('en-GB');
      doc.text(`${eventDate} • ${event.time || 'Time TBD'} • ${event.location || 'Location TBD'}`, margin, 28);

      // PTA logo top-right
      if (ptaInfo?.logo_url) {
        try {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          await new Promise<void>((resolve, reject) => {
            logoImg.onload = () => resolve();
            logoImg.onerror = () => reject();
            logoImg.src = ptaInfo.logo_url;
          });
          const canvas = document.createElement('canvas');
          canvas.width = logoImg.width;
          canvas.height = logoImg.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(logoImg, 0, 0);
          const logoData = canvas.toDataURL('image/png');
          const logoHeight = 20;
          const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
          doc.addImage(logoData, 'PNG', pageWidth - margin - logoWidth, 8, logoWidth, logoHeight);
        } catch (e) {
          console.warn('Could not load PTA logo for PDF', e);
        }
      }

      doc.setTextColor(0, 0, 0);
      yPos = 38;

      // Event Description
      if (event.description) {
        drawSectionHeader('EVENT DESCRIPTION');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const descLines = doc.splitTextToSize(event.description, maxWidth - 10);
        descLines.forEach((line: string) => {
          checkPage(6);
          doc.text(line, margin + 5, yPos);
          yPos += 5;
        });
        yPos += 5;
      }

      // Resource Summary
      drawSectionHeader('RESOURCE SUMMARY');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const col1 = margin + 8;
      const col2 = margin + maxWidth / 2 + 8;
      doc.text(`Volunteers Needed:  ${totalVolunteers + helpers.length}`, col1, yPos + 6);
      doc.text(`Cash Floats:  ${floats.length || totalFloatsNeeded}`, col2, yPos + 6);
      doc.text(`PTA Stalls:  ${assignedStalls.length}`, col1, yPos + 14);
      doc.text(`Catering Items:  ${cateringItems.length}`, col2, yPos + 14);
      doc.text(`External Vendors:  ${externalStalls?.length || 0}`, col1, yPos + 22);
      doc.text(`Registered Helpers:  ${helpers.length}`, col2, yPos + 22);
      yPos += 32;

      // PTA Stalls
      if (assignedStalls.length > 0) {
        drawSectionHeader('PTA STALLS');
        assignedStalls.forEach((stall: any) => {
          checkPage(20);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const stallInfo = [];
          if (stall.volunteers_required) stallInfo.push(`${stall.volunteers_required} volunteers`);
          if (stall.floats_needed) stallInfo.push(`${stall.floats_needed} float(s)`);
          const stallLine = stallInfo.length > 0 
            ? `${stall.name} — ${stallInfo.join(', ')}`
            : stall.name;
          doc.text(`• ${stallLine}`, margin + 5, yPos);
          yPos += 5;

          // Equipment for this stall
          if (stall.utilities && stall.utilities.length > 0) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`  Equipment: ${stall.utilities.join(', ')}`, margin + 10, yPos);
            doc.setTextColor(0, 0, 0);
            yPos += 6;
          }
        });
        yPos += 3;
      }

      // Equipment Totals
      if (Object.keys(equipmentSummary).length > 0) {
        drawSectionHeader('EQUIPMENT TOTALS');
        Object.entries(equipmentSummary).forEach(([name, qty]) => {
          drawBullet(`${name}: ${qty}`);
        });
        yPos += 3;
      }

      // Registered Helpers
      if (helpers.length > 0) {
        drawSectionHeader('REGISTERED HELPERS');
        helpers.forEach((helper) => {
          drawBullet(`${helper.name} — ${helper.role}`);
        });
        yPos += 3;
      }

      // Cash Floats
      if (floats.length > 0) {
        drawSectionHeader('CASH FLOATS');
        floats.forEach((f: any) => {
          drawBullet(`${f.float_name} — £${Number(f.amount).toFixed(2)} (${f.status})`);
        });
        yPos += 3;
      }

      // External Vendors
      if (externalStalls && externalStalls.length > 0) {
        drawSectionHeader('EXTERNAL VENDORS');
        externalStalls.forEach((vendor: any) => {
          const statusLabel = vendor.status ? vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1) : 'Pending';
          drawBullet(`${vendor.business_name} — ${statusLabel}`);
        });
        yPos += 3;
      }

      // Catering
      if (cateringItems.length > 0) {
        drawSectionHeader('CATERING');
        cateringItems.forEach((item) => {
          const notesText = item.notes ? ` (${item.notes})` : '';
          drawBullet(`${item.name} — Qty: ${item.quantity}${notesText}`);
        });
        yPos += 3;
      }

      // Ticketing info
      if (event.is_ticketed) {
        drawSectionHeader('TICKETING');
        drawBullet(`Ticket Price: ${event.ticket_price ? `£${Number(event.ticket_price).toFixed(2)}` : 'Free'}`);
        if (event.max_tickets) drawBullet(`Max Tickets: ${event.max_tickets}`);
        drawBullet(`Tickets Sold: ${event.tickets_sold || 0}`);
        yPos += 3;
      }

      // Footer on all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`PTA Hub Event Summary • Page ${i}/${pageCount}`, margin, 290);
        doc.text(new Date().toLocaleDateString('en-GB'), pageWidth - margin - 25, 290);
      }

      const fileName = `${event.name.replace(/\s+/g, '-')}-Summary-${eventDate.replace(/\//g, '-')}.pdf`;
      doc.save(fileName);
      toast.success('Event summary PDF downloaded!');
    } catch (err) {
      console.error('Error generating summary PDF:', err);
      toast.error('Failed to generate summary PDF');
    }
  };

  const downloadEventSummaryWord = async (event: any) => {
    try {
      toast.info('Generating Word summary...');

      const assignedStalls = stallAssignments[event.id] || [];

      const { data: externalStalls } = await supabase
        .from('stall_applications')
        .select('business_name, status')
        .eq('event_id', event.id);

      const { data: cateringData } = await supabase
        .from('event_catering_assignments')
        .select(`
          quantity,
          notes,
          catering_stock (item_name),
          catering_menu_items (name),
          catering_stock_items (item_name)
        `)
        .eq('event_id', event.id);

      const { data: helpersData } = await supabase
        .from('event_helpers')
        .select('volunteer_name, role_task, members (full_name)')
        .eq('event_id', event.id);

      const { data: floatsData } = await supabase
        .from('cash_floats')
        .select('float_name, amount, status')
        .eq('event_id', event.id);

      let totalVolunteers = 0;
      let totalFloatsNeeded = 0;
      const equipmentSummary: Record<string, number> = {};

      assignedStalls.forEach((stall: any) => {
        totalVolunteers += stall.volunteers_required || 0;
        totalFloatsNeeded += stall.floats_needed || 0;
        (stall.utilities || []).forEach((util: string) => {
          const match = util.match(/^(.+?)\s*\((\d+)\)$/);
          const name = match ? match[1] : util;
          const qty = match ? parseInt(match[2]) : 1;
          equipmentSummary[name] = (equipmentSummary[name] || 0) + qty;
        });
      });

      const cateringItems = cateringData?.map((item: any) => ({
        name: item.catering_stock?.item_name || item.catering_menu_items?.name || item.catering_stock_items?.item_name || 'Unknown Item',
        quantity: item.quantity,
        notes: item.notes
      })) || [];

      const helpers = helpersData?.map((h: any) => ({
        name: h.volunteer_name || h.members?.full_name || 'Unknown',
        role: h.role_task || 'General Helper'
      })) || [];

      const floats = floatsData || [];
      const eventDate = new Date(event.event_date).toLocaleDateString('en-GB');

      const sectionHeader = (title: string) => `
        <h2 style="color: #3b82f6; font-size: 13pt; font-family: Calibri, sans-serif; margin: 16pt 0 4pt 0; padding-bottom: 4pt; border-bottom: 1.5pt solid #3b82f6;">${title}</h2>`;

      const bullet = (text: string) => `<p style="font-size: 11pt; font-family: Calibri, sans-serif; margin: 2pt 0 2pt 12pt;">• ${text}</p>`;

      let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${event.name} Summary</title></head>
<body style="font-family: Calibri, sans-serif; margin: 40pt;">
<h1 style="color: #3b82f6; font-size: 22pt; margin-bottom: 2pt;">${event.name}</h1>
<p style="color: #787878; font-size: 11pt; margin-top: 0;">${eventDate} • ${event.time || 'Time TBD'} • ${event.location || 'Location TBD'}</p>`;

      if (event.description) {
        html += sectionHeader('EVENT DESCRIPTION');
        html += `<p style="font-size: 11pt; margin-left: 12pt;">${event.description}</p>`;
      }

      html += sectionHeader('RESOURCE SUMMARY');
      html += `<table style="font-size: 11pt; margin-left: 12pt; border-collapse: collapse;">
        <tr><td style="padding: 2pt 24pt 2pt 0;">Volunteers Needed: ${totalVolunteers + helpers.length}</td><td style="padding: 2pt 0;">Cash Floats: ${floats.length || totalFloatsNeeded}</td></tr>
        <tr><td style="padding: 2pt 24pt 2pt 0;">PTA Stalls: ${assignedStalls.length}</td><td style="padding: 2pt 0;">Catering Items: ${cateringItems.length}</td></tr>
        <tr><td style="padding: 2pt 24pt 2pt 0;">External Vendors: ${externalStalls?.length || 0}</td><td style="padding: 2pt 0;">Registered Helpers: ${helpers.length}</td></tr>
      </table>`;

      if (assignedStalls.length > 0) {
        html += sectionHeader('PTA STALLS');
        assignedStalls.forEach((stall: any) => {
          const info = [];
          if (stall.volunteers_required) info.push(`${stall.volunteers_required} volunteers`);
          if (stall.floats_needed) info.push(`${stall.floats_needed} float(s)`);
          const line = info.length > 0 ? `${stall.name} — ${info.join(', ')}` : stall.name;
          html += bullet(line);
          if (stall.utilities?.length > 0) {
            html += `<p style="font-size: 10pt; color: #646464; margin: 0 0 2pt 24pt;">Equipment: ${stall.utilities.join(', ')}</p>`;
          }
        });
      }

      if (Object.keys(equipmentSummary).length > 0) {
        html += sectionHeader('EQUIPMENT TOTALS');
        Object.entries(equipmentSummary).forEach(([name, qty]) => { html += bullet(`${name}: ${qty}`); });
      }

      if (helpers.length > 0) {
        html += sectionHeader('REGISTERED HELPERS');
        helpers.forEach((h) => { html += bullet(`${h.name} — ${h.role}`); });
      }

      if (floats.length > 0) {
        html += sectionHeader('CASH FLOATS');
        floats.forEach((f: any) => { html += bullet(`${f.float_name} — £${Number(f.amount).toFixed(2)} (${f.status})`); });
      }

      if (externalStalls && externalStalls.length > 0) {
        html += sectionHeader('EXTERNAL VENDORS');
        externalStalls.forEach((v: any) => {
          const statusLabel = v.status ? v.status.charAt(0).toUpperCase() + v.status.slice(1) : 'Pending';
          html += bullet(`${v.business_name} — ${statusLabel}`);
        });
      }

      if (cateringItems.length > 0) {
        html += sectionHeader('CATERING');
        cateringItems.forEach((item) => {
          const notesText = item.notes ? ` (${item.notes})` : '';
          html += bullet(`${item.name} — Qty: ${item.quantity}${notesText}`);
        });
      }

      if (event.is_ticketed) {
        html += sectionHeader('TICKETING');
        html += bullet(`Ticket Price: ${event.ticket_price ? `£${Number(event.ticket_price).toFixed(2)}` : 'Free'}`);
        if (event.max_tickets) html += bullet(`Max Tickets: ${event.max_tickets}`);
        html += bullet(`Tickets Sold: ${event.tickets_sold || 0}`);
      }

      html += `</body></html>`;

      const blob = new Blob([html], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/\s+/g, '-')}-Summary-${eventDate.replace(/\//g, '-')}.doc`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Event summary Word document downloaded!');
    } catch (err) {
      console.error('Error generating summary Word:', err);
      toast.error('Failed to generate summary Word document');
    }
  };

  const handleAssignStall = async (eventId: string) => {
    if (!selectedStall) return;

    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) {
      toast.error("Could not find your PTA");
      return;
    }

    const { error } = await supabase
      .from('event_stall_assignments')
      .insert({
        event_id: eventId,
        stall_id: selectedStall.id,
        pta_id: memberData.pta_id,
        created_by: user?.id,
      });

    if (error) {
      if (error.code === '23505') {
        toast.error("This stall is already assigned to this event");
      } else {
        toast.error("Failed to assign stall");
        console.error(error);
      }
    } else {
      toast.success(`${selectedStall.name} assigned to event`);
      fetchStallAssignments();
    }

    setAssignStallDialogOpen(false);
    setSelectedStall(null);
  };

  const handleAssignStallsToEvent = async () => {
    if (!selectedEventForStallAssign || selectedStallIds.length === 0) return;

    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) {
      toast.error("Could not find your PTA");
      return;
    }

    const rows = selectedStallIds.map(stallId => ({
      event_id: selectedEventForStallAssign.id,
      stall_id: stallId,
      pta_id: memberData.pta_id,
      created_by: user?.id,
    }));

    const { error } = await supabase
      .from('event_stall_assignments')
      .insert(rows);

    if (error) {
      if (error.code === '23505') {
        toast.error("Some stalls are already assigned to this event");
      } else {
        toast.error("Failed to assign stalls");
        console.error(error);
      }
    } else {
      toast.success(`${selectedStallIds.length} stall${selectedStallIds.length !== 1 ? 's' : ''} assigned to ${selectedEventForStallAssign.name}`);
      fetchStallAssignments();
    }

    setAssignStallToEventDialogOpen(false);
    setSelectedEventForStallAssign(null);
    setSelectedStallIds([]);
  };

  const fetchStallAssignments = async () => {
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { data, error } = await supabase
      .from('event_stall_assignments')
      .select(`
        id,
        event_id,
        stall_id,
        reusable_stalls (id, name, volunteers_required, floats_needed, utilities, price_tiers)
      `)
      .eq('pta_id', memberData.pta_id);

    if (!error && data) {
      const assignments: Record<string, any[]> = {};
      data.forEach((item: any) => {
        if (!assignments[item.event_id]) {
          assignments[item.event_id] = [];
        }
        if (item.reusable_stalls) {
          assignments[item.event_id].push({
            ...item.reusable_stalls,
            assignment_id: item.id
          });
        }
      });
      setStallAssignments(assignments);
    }
  };

  const handleRemoveStallAssignment = async (assignmentId: string, eventId: string) => {
    const { error } = await supabase
      .from('event_stall_assignments')
      .delete()
      .eq('id', assignmentId);

    if (error) {
      toast.error("Failed to remove stall");
    } else {
      toast.success("Stall removed from event");
      fetchStallAssignments();
    }
  };

  const fetchReusableStalls = async () => {
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { data, error } = await supabase
      .from('reusable_stalls')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setReusableStalls(data);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchReusableStalls();
      fetchStallAssignments();
    }
  }, [user]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold text-foreground">Events Dashboard</h1>
      </div>

      <Tabs defaultValue="events" className="w-full">
        <TabsList>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="stalls">Stalls</TabsTrigger>
          <TabsTrigger value="helpers">Helpers</TabsTrigger>
          {isStandardPlan && <TabsTrigger value="ticketing">Ticketing</TabsTrigger>}
          {isStandardPlan && <TabsTrigger value="planner">Event Planner</TabsTrigger>}
        </TabsList>

        <TabsContent value="events" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Manage Events</CardTitle>
                  <CardDescription>Create, edit, and organize PTA events</CardDescription>
                </div>
                <div className="flex gap-2">
                  {showArchived && (
                    <Button variant="outline" onClick={() => setShowArchived(false)}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Active Events
                    </Button>
                  )}
                  {!showArchived && (
                    <Button variant="outline" onClick={() => setShowArchived(true)}>
                      <Archive className="h-4 w-4 mr-2" />
                      View Archive
                    </Button>
                  )}
                  <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Copy className="h-4 w-4 mr-2" />
                        Use Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Select Event Template</DialogTitle>
                        <DialogDescription>Choose a pre-configured event template</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        {templates.length === 0 ? (
                          <p className="text-center text-muted-foreground py-4">
                            No templates saved yet. Save an event as a template to reuse it later.
                          </p>
                        ) : (
                          templates.map((template) => (
                            <div 
                              key={template.id}
                              className="p-3 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer"
                              onClick={() => {
                                setSelectedTemplate(template);
                                setTemplateDialogOpen(false);
                                setCreateFromTemplateOpen(true);
                              }}
                            >
                              <h4 className="font-semibold">{template.name}</h4>
                              <p className="text-sm text-muted-foreground">{template.description || 'No description'}</p>
                              {template.location && (
                                <p className="text-sm text-muted-foreground">Location: {template.location}</p>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        New Event
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Create New Event</DialogTitle>
                        <DialogDescription>Set up a new PTA event</DialogDescription>
                      </DialogHeader>
                      <form 
                        className="space-y-4"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const formData = new FormData(e.currentTarget);
                          const isTicketed = formData.get('is-ticketed') === 'on';
                          
                          const { data: memberData } = await supabase
                            .from('members')
                            .select('pta_id')
                            .eq('user_id', user?.id)
                            .single();

                          const { error } = await supabase
                            .from('events')
                            .insert({
                              name: formData.get('event-name') as string,
                              event_date: formData.get('event-date') as string,
                              time: formData.get('event-time') as string,
                              location: formData.get('event-location') as string,
                              description: formData.get('event-description') as string,
                              pta_id: memberData?.pta_id,
                              status: 'active',
                              is_ticketed: isTicketed,
                              max_tickets: isTicketed ? parseInt(formData.get('max-tickets') as string) || null : null,
                              named_tickets_required: isTicketed && formData.get('named-tickets') === 'on',
                              show_ticket_availability: formData.get('show-ticket-availability') === 'on' || !isTicketed,
                              show_free_label: formData.get('show-free-label') === 'on',
                            });

                          if (error) {
                            toast.error("Failed to create event");
                            console.error(error);
                          } else {
                            toast.success("Event created successfully");
                            fetchEvents();
                          }
                        }}
                      >
                        <div className="space-y-2">
                          <Label htmlFor="event-name">Event Name</Label>
                          <Input id="event-name" name="event-name" placeholder="e.g., Summer Fair 2025" required />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="event-date">Date</Label>
                            <Input id="event-date" name="event-date" type="date" required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="event-time">Time</Label>
                            <Input id="event-time" name="event-time" type="time" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event-location">Location</Label>
                          <Input id="event-location" name="event-location" placeholder="e.g., School Field" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event-description">Description</Label>
                          <Textarea id="event-description" name="event-description" placeholder="Event details..." rows={3} />
                        </div>
                        
                        {isStandardPlan && (
                        <>
                        {/* Ticketing Options */}
                        <div className="space-y-4 border-t pt-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="is-ticketed">Ticketed Event</Label>
                              <p className="text-sm text-muted-foreground">Enable ticket sales for this event</p>
                            </div>
                            <Switch id="is-ticketed" name="is-ticketed" onCheckedChange={(checked) => {
                              const maxTicketsField = document.getElementById('max-tickets-container');
                              const namedTicketsField = document.getElementById('named-tickets-container');
                              const showToggles = document.getElementById('show-toggles-container');
                              if (maxTicketsField && namedTicketsField) {
                                maxTicketsField.style.display = checked ? 'block' : 'none';
                                namedTicketsField.style.display = checked ? 'flex' : 'none';
                              }
                              if (showToggles) {
                                showToggles.style.display = checked ? 'block' : 'none';
                              }
                            }} />
                          </div>
                          
                          <div id="max-tickets-container" className="space-y-2" style={{ display: 'none' }}>
                            <Label htmlFor="max-tickets">Maximum Tickets Available</Label>
                            <Input id="max-tickets" name="max-tickets" type="number" min="1" placeholder="e.g., 100" />
                          </div>
                          
                          <div id="named-tickets-container" className="items-center justify-between" style={{ display: 'none' }}>
                            <div className="space-y-0.5">
                              <Label htmlFor="named-tickets">Named Tickets</Label>
                              <p className="text-sm text-muted-foreground">Require attendee names at purchase</p>
                            </div>
                            <Switch id="named-tickets" name="named-tickets" />
                          </div>
                          
                          {/* Show/Hide Toggles */}
                          <div id="show-toggles-container" className="space-y-3" style={{ display: 'none' }}>
                            <div className="flex items-center justify-between">
                              <div className="space-y-0.5">
                                <Label htmlFor="show-ticket-availability">Show Ticket Availability</Label>
                                <p className="text-sm text-muted-foreground">Display remaining tickets on fundraising page</p>
                              </div>
                              <Switch id="show-ticket-availability" name="show-ticket-availability" defaultChecked />
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="show-free-label">Show "Free" Label</Label>
                              <p className="text-sm text-muted-foreground">Display "Free" badge on event card when no ticket price</p>
                            </div>
                            <Switch id="show-free-label" name="show-free-label" defaultChecked />
                          </div>
                        </div>
                        </>
                        )}
                        
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="outline">Cancel</Button>
                          </DialogClose>
                          <DialogClose asChild>
                            <Button type="submit">Create Event</Button>
                          </DialogClose>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {showArchived && (
                <div className="mb-4 p-3 bg-muted rounded-lg border border-border">
                  <h3 className="font-semibold text-muted-foreground flex items-center gap-2">
                    <Archive className="h-4 w-4" />
                    Archived Events
                  </h3>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Our Stalls</TableHead>
                    {showExternalStalls && <TableHead>External Stalls</TableHead>}
                    {showCatering && <TableHead>Catering</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4 + (showExternalStalls ? 1 : 0) + (showCatering ? 1 : 0)} className="text-center text-muted-foreground py-8">
                        No events yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {event.name}
                            {isStandardPlan && event.is_ticketed && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Ticket className="h-4 w-4 text-primary" />
                                  </TooltipTrigger>
                                  <TooltipContent>Ticketed event</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{new Date(event.event_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="group flex items-center gap-2 hover:bg-accent"
                                    >
                                      <span className="font-medium group-hover:text-accent-foreground">{stallAssignments[event.id]?.length || 0}</span>
                                      <span className="text-sm text-muted-foreground group-hover:text-accent-foreground">
                                        {(stallAssignments[event.id]?.length || 0) === 1 ? 'stall' : 'stalls'}
                                      </span>
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72">
                                    <div className="space-y-2">
                                      <h4 className="font-medium">Assigned Stalls</h4>
                                      {(!stallAssignments[event.id] || stallAssignments[event.id].length === 0) ? (
                                        <p className="text-sm text-muted-foreground">No stalls assigned yet. Add stalls from the Stalls tab.</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {stallAssignments[event.id].map((stall: any) => (
                                            <div key={stall.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                              <span className="text-sm font-medium">{stall.name}</span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={() => handleRemoveStallAssignment(stall.assignment_id, event.id)}
                                              >
                                                <X className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Click to see assigned PTA stalls</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        {showExternalStalls && (
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewApplications(event)}
                                  className="group flex items-center gap-2 hover:bg-accent"
                                >
                                  <span className="font-medium group-hover:text-accent-foreground">{stallCounts[event.id] || 0}</span>
                                  <span className="text-sm text-muted-foreground group-hover:text-accent-foreground">
                                    {(stallCounts[event.id] || 0) === 1 ? 'application' : 'applications'}
                                  </span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Click to see external stall applications</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        )}
                        {showCatering && (
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewCatering(event)}
                                  className="group flex items-center gap-2 hover:bg-accent"
                                >
                                  <span className="font-medium group-hover:text-accent-foreground">{cateringCounts[event.id] || 0}</span>
                                  <span className="text-sm text-muted-foreground group-hover:text-accent-foreground">
                                    {(cateringCounts[event.id] || 0) === 1 ? 'item' : 'items'}
                                  </span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Click to see catering allocated to this event</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        )}
                        <TableCell>
                          <TooltipProvider>
                            <div className="flex gap-1">
                              {!showArchived && (
                                <>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleEditEvent(event)}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit event</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={(e) => openSaveTemplateDialog(event, e)}
                                      >
                                        <Save className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Save as template</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => {
                                          setSelectedEventForStallAssign(event);
                                          setSelectedStallIds([]);
                                          setAssignStallToEventDialogOpen(true);
                                        }}
                                      >
                                        <Store className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Assign stall</TooltipContent>
                                  </Tooltip>
                                  {new Date(event.event_date) < new Date() && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={(e) => handleArchiveEvent(event.id, e)}
                                        >
                                          <Archive className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Archive event</TooltipContent>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                              {showArchived && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={(e) => handleRestoreEvent(event.id, e)}
                                    >
                                      <ArchiveRestore className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Restore event</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={(e) => handleDeleteEvent(event.id, e)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete event</TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stalls" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Reusable Stalls</CardTitle>
                  <CardDescription>Configure stall requirements and resources</CardDescription>
                </div>
                <Dialog open={addStallDialogOpen} onOpenChange={(open) => {
                  setAddStallDialogOpen(open);
                  if (!open) {
                    setSelectedUtilities([]);
                    setPriceTiers([]);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Stall
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Stall</DialogTitle>
                      <DialogDescription>Create a reusable stall configuration</DialogDescription>
                    </DialogHeader>
                    <form className="space-y-4" onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      
                      const { data: memberData } = await supabase
                        .from('members')
                        .select('pta_id')
                        .eq('user_id', user?.id)
                        .single();

                      if (!memberData?.pta_id) {
                        toast.error("Could not find your PTA");
                        return;
                      }

                      const utilitiesArray = selectedUtilities.map(u => 
                        u.qty > 1 ? `${u.name} (${u.qty})` : u.name
                      );

                      const { error } = await supabase
                        .from('reusable_stalls')
                        .insert({
                          pta_id: memberData.pta_id,
                          name: formData.get('stall-name') as string,
                          volunteers_required: parseInt(formData.get('stall-volunteers') as string) || 1,
                          floats_needed: parseInt(formData.get('stall-float') as string) || 0,
                          utilities: utilitiesArray,
                          price_tiers: priceTiers.filter(t => t.description && t.price > 0),
                          created_by: user?.id,
                        });

                      if (error) {
                        toast.error("Failed to create stall");
                        console.error(error);
                      } else {
                        toast.success("Stall added successfully");
                        fetchReusableStalls();
                        setAddStallDialogOpen(false);
                        setSelectedUtilities([]);
                      }
                    }}>
                      <div className="space-y-2">
                        <Label htmlFor="stall-name">Stall Name</Label>
                        <Input id="stall-name" name="stall-name" placeholder="e.g., BBQ Stand" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stall-volunteers">Volunteers Required</Label>
                        <Input id="stall-volunteers" name="stall-volunteers" type="number" placeholder="e.g., 3" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stall-float">Number of Floats Needed</Label>
                        <Input id="stall-float" name="stall-float" type="number" placeholder="e.g., 2" />
                      </div>
                      <div className="space-y-2">
                        <Label>Utilities and Equipment</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start h-auto min-h-10 py-2">
                              {selectedUtilities.length === 0 ? (
                                <span className="text-muted-foreground">Select utilities...</span>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {selectedUtilities.map((utility) => (
                                    <Badge key={utility.name} variant="secondary" className="mr-1">
                                      {utility.name} {utility.qty > 1 && `(${utility.qty})`}
                                      <button
                                        type="button"
                                        className="ml-1 hover:text-destructive"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedUtilities(selectedUtilities.filter(u => u.name !== utility.name));
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 p-3 bg-popover border border-border shadow-lg z-50" align="start">
                            <div className="space-y-3">
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {availableUtilities.map((utility) => {
                                  const selected = selectedUtilities.find(u => u.name === utility);
                                  const hideQty = ["Power", "Water", "Gas", "WiFi", "Lighting"].includes(utility);
                                  return (
                                    <div key={utility} className="flex items-center justify-between gap-2">
                                      <div className="flex items-center space-x-2 flex-1">
                                        <Checkbox
                                          id={`utility-${utility}`}
                                          checked={!!selected}
                                          onCheckedChange={(checked) => {
                                            if (checked) {
                                              setSelectedUtilities([...selectedUtilities, {name: utility, qty: 1}]);
                                            } else {
                                              setSelectedUtilities(selectedUtilities.filter(u => u.name !== utility));
                                            }
                                          }}
                                        />
                                        <label htmlFor={`utility-${utility}`} className="text-sm cursor-pointer flex-1">
                                          {utility}
                                        </label>
                                      </div>
                                      {selected && !hideQty && (
                                        <Input
                                          type="number"
                                          min="1"
                                          value={selected.qty}
                                          onChange={(e) => {
                                            const newQty = parseInt(e.target.value) || 1;
                                            setSelectedUtilities(selectedUtilities.map(u => 
                                              u.name === utility ? {...u, qty: newQty} : u
                                            ));
                                          }}
                                          className="w-16 h-7 text-sm text-center"
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="border-t border-border pt-3">
                                <Label className="text-xs text-muted-foreground mb-2 block">Add custom utility</Label>
                                <div className="flex gap-2">
                                  <Input
                                    value={customUtilityInput}
                                    onChange={(e) => setCustomUtilityInput(e.target.value)}
                                    placeholder="Custom utility..."
                                    className="h-8 text-sm"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (customUtilityInput.trim() && !availableUtilities.includes(customUtilityInput.trim())) {
                                          const newUtility = customUtilityInput.trim();
                                          setAvailableUtilities([...availableUtilities, newUtility]);
                                          setSelectedUtilities([...selectedUtilities, {name: newUtility, qty: 1}]);
                                          setCustomUtilityInput("");
                                        }
                                      }
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    className="h-8 px-2"
                                    onClick={() => {
                                      if (customUtilityInput.trim() && !availableUtilities.includes(customUtilityInput.trim())) {
                                        const newUtility = customUtilityInput.trim();
                                        setAvailableUtilities([...availableUtilities, newUtility]);
                                        setSelectedUtilities([...selectedUtilities, {name: newUtility, qty: 1}]);
                                        setCustomUtilityInput("");
                                      }
                                    }}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <input type="hidden" name="stall-utilities" value={selectedUtilities.map(u => u.qty > 1 ? `${u.name} (${u.qty})` : u.name).join(", ")} />
                      </div>
                      <div className="space-y-2">
                        <Label>Pricing (Optional)</Label>
                        <p className="text-xs text-muted-foreground">Add pricing options for this stall (e.g., "£1 for 1 go, £2 for 3 goes")</p>
                        <div className="space-y-2">
                          {priceTiers.map((tier, index) => (
                            <div key={index} className="flex gap-2 items-center">
                              <Input
                                placeholder="Description (e.g., 1 go)"
                                value={tier.description}
                                onChange={(e) => {
                                  const newTiers = [...priceTiers];
                                  newTiers[index].description = e.target.value;
                                  setPriceTiers(newTiers);
                                }}
                                className="flex-1"
                              />
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">£</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={tier.price || ''}
                                  onChange={(e) => {
                                    const newTiers = [...priceTiers];
                                    newTiers[index].price = parseFloat(e.target.value) || 0;
                                    setPriceTiers(newTiers);
                                  }}
                                  className="w-20"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setPriceTiers(priceTiers.filter((_, i) => i !== index))}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPriceTiers([...priceTiers, { description: '', price: 0 }])}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Price Tier
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Add Stall</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {reusableStalls.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No stalls created yet. Add your first reusable stall above.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Stall Name</TableHead>
                        <TableHead className="text-center">Volunteers</TableHead>
                        <TableHead className="text-center">Floats</TableHead>
                        <TableHead>Utilities & Equipment</TableHead>
                        <TableHead>Pricing</TableHead>
                              <TableHead className="text-left">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reusableStalls.map((stall) => (
                        <TableRow key={stall.id}>
                          <TableCell className="font-medium">{stall.name}</TableCell>
                          <TableCell className="text-center">{stall.volunteers_required}</TableCell>
                          <TableCell className="text-center">{stall.floats_needed}</TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {stall.utilities?.length > 0 ? stall.utilities.join(', ') : 'None'}
                          </TableCell>
                          <TableCell>
                            {stall.price_tiers && stall.price_tiers.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {stall.price_tiers.slice(0, 2).map((tier: any, idx: number) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {tier.description}: £{tier.price.toFixed(2)}
                                  </Badge>
                                ))}
                                {stall.price_tiers.length > 2 && (
                                  <Badge variant="secondary" className="text-xs">+{stall.price_tiers.length - 2}</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <TooltipProvider>
                              <div className="flex gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setEditingStall(stall);
                                        const parsedUtilities = (stall.utilities || []).map((u: string) => {
                                          const match = u.match(/^(.+?)\s*\((\d+)\)$/);
                                          if (match) {
                                            return { name: match[1], qty: parseInt(match[2]) };
                                          }
                                          return { name: u, qty: 1 };
                                        });
                                        setEditStallUtilities(parsedUtilities);
                                        setEditPriceTiers(stall.price_tiers || []);
                                        setEditStallDialogOpen(true);
                                      }}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit stall</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={async () => {
                                        const { error } = await supabase
                                          .from('reusable_stalls')
                                          .delete()
                                          .eq('id', stall.id);
                                        
                                        if (error) {
                                          toast.error("Failed to delete stall");
                                        } else {
                                          toast.success("Stall deleted");
                                          fetchReusableStalls();
                                        }
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete stall</TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="helpers" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Helper Sign-Ups</CardTitle>
                <CardDescription>Manage volunteer registrations for events</CardDescription>
              </div>
              <Dialog open={addHelperDialogOpen} onOpenChange={setAddHelperDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" disabled={!selectedHelperEvent}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Helper
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Helper</DialogTitle>
                    <DialogDescription>Add a member or external volunteer to help with this event</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const memberId = formData.get('member-id') as string;
                    const volunteerName = formData.get('volunteer-name') as string;
                    const volunteerEmail = formData.get('volunteer-email') as string;
                    const roleTask = formData.get('role-task') as string;
                    
                    const insertData: any = {
                      event_id: selectedHelperEvent,
                      pta_id: ptaId,
                      role_task: roleTask || null,
                    };
                    
                    if (memberId && memberId !== 'external') {
                      insertData.member_id = memberId;
                    } else {
                      insertData.volunteer_name = volunteerName;
                      insertData.volunteer_email = volunteerEmail;
                    }
                    
                    const { error } = await supabase.from('event_helpers').insert(insertData);
                    
                    if (error) {
                      toast.error("Failed to add helper");
                      console.error(error);
                    } else {
                      toast.success("Helper added");
                      setAddHelperDialogOpen(false);
                      fetchEventHelpers(selectedHelperEvent);
                    }
                  }}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Select Member (or leave blank for external volunteer)</Label>
                        <Select name="member-id">
                          <SelectTrigger>
                            <SelectValue placeholder="Select a member..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="external">External Volunteer</SelectItem>
                            {members.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="volunteer-name">Volunteer Name (if external)</Label>
                        <Input id="volunteer-name" name="volunteer-name" placeholder="Full name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="volunteer-email">Volunteer Email (if external)</Label>
                        <Input id="volunteer-email" name="volunteer-email" type="email" placeholder="email@example.com" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="role-task">Role / Task</Label>
                        <Input id="role-task" name="role-task" placeholder="e.g., BBQ, Face Painting" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Add Helper</Button>
                      </div>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Event Selector */}
                <div className="space-y-2">
                  <Label>Select Event</Label>
                  <Select value={selectedHelperEvent} onValueChange={(value) => {
                    setSelectedHelperEvent(value);
                    if (value) fetchEventHelpers(value);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an event..." />
                    </SelectTrigger>
                    <SelectContent>
                      {events.filter(e => !e.is_archived).map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.name} - {new Date(event.event_date).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedHelperEvent && (
                  <>
                    {/* Sign-up Link */}
                    <TooltipProvider>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        Public Volunteer Sign-Up Link
                      </h4>
                      <div className="flex gap-2">
                        <Input 
                          value={`${window.location.origin}/v/${ptaId.substring(0, 8)}/${selectedHelperEvent}`} 
                          readOnly 
                          className="font-mono text-sm"
                        />
                        <Button 
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/v/${ptaId.substring(0, 8)}/${selectedHelperEvent}`);
                            toast.success("Link copied!");
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline"
                              onClick={() => {
                                const event = events.find(e => e.id === selectedHelperEvent);
                                if (!event) return;
                                
                                const doc = new jsPDF();
                                const pageWidth = doc.internal.pageSize.getWidth();
                                let y = 20;

                                // PTA Name header
                                doc.setFontSize(20);
                                doc.setFont("helvetica", "bold");
                                doc.text(ptaInfo?.name || "PTA", pageWidth / 2, y, { align: "center" });
                                y += 10;

                                // Title
                                doc.setFontSize(16);
                                doc.text("Volunteer Sign-Up Form", pageWidth / 2, y, { align: "center" });
                                y += 12;

                                // Divider
                                doc.setDrawColor(200, 200, 200);
                                doc.setLineWidth(0.5);
                                doc.line(20, y, pageWidth - 20, y);
                                y += 10;

                                // Event details
                                doc.setFontSize(14);
                                doc.setFont("helvetica", "bold");
                                doc.text(event.name, 20, y);
                                y += 8;

                                doc.setFontSize(11);
                                doc.setFont("helvetica", "normal");
                                const eventDate = new Date(event.event_date);
                                doc.text(`Date: ${eventDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`, 20, y);
                                y += 6;
                                if (event.time) {
                                  doc.text(`Time: ${event.time}`, 20, y);
                                  y += 6;
                                }
                                if (event.location) {
                                  doc.text(`Location: ${event.location}`, 20, y);
                                  y += 6;
                                }
                                y += 8;

                                // Instructions
                                doc.setFontSize(10);
                                doc.setFont("helvetica", "italic");
                                doc.text("Please fill in your details below if you'd like to volunteer at this event.", 20, y);
                                y += 10;

                                // Form rows
                                const rowHeight = 18;
                                const labelX = 20;
                                const fieldX = 65;
                                const fieldWidth = pageWidth - fieldX - 20;
                                const rows = ["Name", "Preferred Role / Task"];
                                
                                // Generate blank sign-up rows to fill the page
                                const availableSpace = 270 - y; // leave room for footer
                                const rowBlockHeight = rows.length * (rowHeight * 0.6) + 8;
                                const numRows = Math.floor(availableSpace / rowBlockHeight);
                                
                                for (let r = 0; r < numRows; r++) {
                                  doc.setDrawColor(180, 180, 180);
                                  doc.line(labelX, y, pageWidth - 20, y);
                                  y += 5;

                                  doc.setFont("helvetica", "normal");
                                  doc.setFontSize(9);
                                  for (const label of rows) {
                                    doc.text(`${label}:`, labelX, y + 4);
                                    doc.setDrawColor(200, 200, 200);
                                    doc.line(fieldX, y + 5, fieldX + fieldWidth, y + 5);
                                    y += rowHeight * 0.6;
                                  }
                                  y += 3;
                                }

                                // Footer
                                const pageHeight = doc.internal.pageSize.getHeight();
                                doc.setFontSize(8);
                                doc.setFont("helvetica", "normal");
                                doc.setTextColor(150, 150, 150);
                                doc.text("Powered by PTA Hub", pageWidth / 2, pageHeight - 10, { align: "center" });

                                doc.save(`Volunteer-Signup-${event.name.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`);
                                toast.success("PDF downloaded!");
                              }}
                            >
                              <FileDown className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Download printable sign-up form</TooltipContent>
                        </Tooltip>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Share this link for external volunteers to sign up, or download a printable form</p>
                    </div>
                    </TooltipProvider>

                    {/* Helpers Table */}
                    <TooltipProvider>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Role / Task</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {eventHelpers.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                No helpers signed up yet
                              </TableCell>
                            </TableRow>
                          ) : (
                            eventHelpers.map((helper) => (
                              <TableRow key={helper.id}>
                                <TableCell className="font-medium">
                                  {helper.member_id ? helper.members?.full_name : helper.volunteer_name}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {helper.member_id ? helper.members?.email : helper.volunteer_email}
                                </TableCell>
                                <TableCell>
                                  {helper.role_task ? (
                                    <Badge variant="secondary">{helper.role_task}</Badge>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge variant={helper.member_id ? "default" : "outline"}>
                                    {helper.member_id ? "Member" : "External"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setEditingHelper(helper);
                                            setEditHelperDialogOpen(true);
                                          }}
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Edit helper</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={async () => {
                                            const { error } = await supabase
                                              .from('event_helpers')
                                              .delete()
                                              .eq('id', helper.id);
                                            
                                            if (error) {
                                              toast.error("Failed to remove helper");
                                            } else {
                                              toast.success("Helper removed");
                                              fetchEventHelpers(selectedHelperEvent);
                                            }
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Remove helper</TooltipContent>
                                    </Tooltip>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Helper Dialog */}
          <Dialog open={editHelperDialogOpen} onOpenChange={setEditHelperDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Helper</DialogTitle>
                <DialogDescription>Update the helper's role or task</DialogDescription>
              </DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!editingHelper) return;
                const formData = new FormData(e.currentTarget);
                const roleTask = formData.get('edit-role-task') as string;
                
                const { error } = await supabase
                  .from('event_helpers')
                  .update({ role_task: roleTask || null })
                  .eq('id', editingHelper.id);
                
                if (error) {
                  toast.error("Failed to update helper");
                } else {
                  toast.success("Helper updated");
                  setEditHelperDialogOpen(false);
                  setEditingHelper(null);
                  fetchEventHelpers(selectedHelperEvent);
                }
              }}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Helper</Label>
                    <p className="text-sm font-medium">
                      {editingHelper?.member_id ? editingHelper?.members?.full_name : editingHelper?.volunteer_name}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-role-task">Role / Task</Label>
                    <Input 
                      id="edit-role-task" 
                      name="edit-role-task" 
                      defaultValue={editingHelper?.role_task || ""}
                      placeholder="e.g., BBQ, Face Painting"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <DialogClose asChild>
                      <Button type="button" variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {isStandardPlan && <TabsContent value="ticketing" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Event Ticketing</CardTitle>
              <CardDescription>Manage tickets for ticketed events</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const ticketedEvents = events.filter(e => e.is_ticketed && !e.is_archived);
                
                if (ticketedEvents.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      <Ticket className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No ticketed events</p>
                      <p className="text-sm mt-1">Create an event and enable the "Ticketed Event" toggle to manage tickets here.</p>
                    </div>
                  );
                }
                
                return (
                  <div className="grid gap-4 md:grid-cols-2">
                    {ticketedEvents.map((event) => (
                      <Card key={event.id} className="overflow-hidden flex flex-col h-full">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg">{event.name}</CardTitle>
                              <CardDescription>
                                {new Date(event.event_date).toLocaleDateString('en-GB', { 
                                  weekday: 'short', 
                                  day: 'numeric', 
                                  month: 'short',
                                  year: 'numeric'
                                })}
                                {event.time && ` • ${event.time}`}
                              </CardDescription>
                            </div>
                            <Badge variant={event.named_tickets_required ? "default" : "secondary"}>
                              {event.named_tickets_required ? "Named" : "General"}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="flex flex-col space-y-4 flex-1">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-3 bg-muted/30 rounded-lg">
                              <p className="text-2xl font-bold text-primary">{event.tickets_sold || 0}</p>
                              <p className="text-xs text-muted-foreground">Tickets Sold</p>
                            </div>
                            <div className="text-center p-3 bg-muted/30 rounded-lg">
                              <p className="text-2xl font-bold">{event.max_tickets || '∞'}</p>
                              <p className="text-xs text-muted-foreground">Max Tickets</p>
                            </div>
                          </div>
                          
                          <div className="min-h-[44px]">
                            {event.max_tickets && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Availability</span>
                                  <span className="font-medium">
                                    {Math.max(0, event.max_tickets - (event.tickets_sold || 0))} remaining
                                  </span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary transition-all"
                                    style={{ 
                                      width: `${Math.min(100, ((event.tickets_sold || 0) / event.max_tickets) * 100)}%` 
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                            <Dialog 
                              open={ticketDialogOpenId === event.id}
                              onOpenChange={async (open) => {
                                if (open) {
                                  setTicketDialogOpenId(event.id);
                                  setSelectedTicketEvent(event.id);
                                  // Fetch price tiers
                                  const { data: tiersData } = await supabase
                                    .from('ticket_price_tiers')
                                    .select('*')
                                    .eq('event_id', event.id)
                                    .order('display_order');
                                  setTicketPriceTiers(tiersData || []);
                                  // Fetch extras
                                  const { data: extrasData } = await supabase
                                    .from('ticket_extras')
                                    .select('*')
                                    .eq('event_id', event.id)
                                    .order('display_order');
                                  setTicketExtras(extrasData || []);
                                } else {
                                  setTicketDialogOpenId(null);
                                }
                              }}
                            >
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" className="flex-1">
                                  <Pencil className="h-4 w-4 mr-1" />
                                  Details
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Ticket Settings - {event.name}</DialogTitle>
                                  <DialogDescription>Configure ticket details, price tiers, and extras for this event</DialogDescription>
                                </DialogHeader>
                                <Tabs defaultValue="settings" className="w-full">
                                  <TabsList className="grid w-full grid-cols-3">
                                    <TabsTrigger value="settings">Settings</TabsTrigger>
                                    <TabsTrigger value="tiers">
                                      <PoundSterling className="h-4 w-4 mr-1" />
                                      Price Tiers
                                    </TabsTrigger>
                                    <TabsTrigger value="extras">
                                      <ShoppingBag className="h-4 w-4 mr-1" />
                                      Extras
                                    </TabsTrigger>
                                  </TabsList>
                                  
                                  <TabsContent value="settings" className="space-y-4 mt-4">
                                    <form 
                                      id={`ticket-settings-form-${event.id}`}
                                      className="space-y-4"
                                      onSubmit={async (e) => {
                                        e.preventDefault();
                                        const formData = new FormData(e.currentTarget);
                                        
                                        const { error } = await supabase
                                          .from('events')
                                          .update({
                                            ticket_price: parseFloat(formData.get('ticket-price') as string) || null,
                                            max_tickets: parseInt(formData.get('max-tickets-edit') as string) || null,
                                            named_tickets_required: formData.get('named-tickets-edit') === 'on',
                                          })
                                          .eq('id', event.id);
                                        
                                        if (error) {
                                          toast.error("Failed to update ticket settings");
                                        } else {
                                          toast.success("Ticket settings updated");
                                          fetchEvents();
                                          setTicketDialogOpenId(null);
                                        }
                                      }}
                                    >
                                      <div className="space-y-2">
                                        <Label htmlFor="ticket-price">Default Ticket Price (£)</Label>
                                        <Input 
                                          id="ticket-price" 
                                          name="ticket-price" 
                                          type="number" 
                                          step="0.01" 
                                          min="0"
                                          placeholder="0.00"
                                          defaultValue={event.ticket_price || ''}
                                        />
                                        <p className="text-xs text-muted-foreground">Used when no price tiers are defined</p>
                                      </div>
                                      <div className="space-y-2">
                                        <Label htmlFor="max-tickets-edit">Maximum Tickets</Label>
                                        <Input 
                                          id="max-tickets-edit" 
                                          name="max-tickets-edit" 
                                          type="number" 
                                          min="1"
                                          placeholder="Leave blank for unlimited"
                                          defaultValue={event.max_tickets || ''}
                                        />
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                          <Label htmlFor="named-tickets-edit">Named Tickets</Label>
                                          <p className="text-sm text-muted-foreground">Require attendee names</p>
                                        </div>
                                        <Switch 
                                          id="named-tickets-edit" 
                                          name="named-tickets-edit"
                                          defaultChecked={event.named_tickets_required}
                                        />
                                      </div>
                                      
                                      {/* Ticket Summary */}
                                      <div className="border-t pt-4 mt-4">
                                        <h4 className="font-semibold mb-3">Ticket Summary</h4>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                          <div className="p-2 bg-muted/30 rounded">
                                            <p className="text-muted-foreground">Tickets Sold</p>
                                            <p className="font-semibold text-lg">{event.tickets_sold || 0}</p>
                                          </div>
                                          <div className="p-2 bg-muted/30 rounded">
                                            <p className="text-muted-foreground">Revenue</p>
                                            <p className="font-semibold text-lg">
                                              £{((event.tickets_sold || 0) * (event.ticket_price || 0)).toFixed(2)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </form>
                                  </TabsContent>
                                  
                                  <TabsContent value="tiers" className="space-y-4 mt-4">
                                    <div className="flex justify-between items-center">
                                      <p className="text-sm text-muted-foreground">Define different ticket prices (e.g., Adult, Child, Senior)</p>
                                      <Button 
                                        size="sm"
                                        onClick={async () => {
                                          const { error } = await supabase
                                            .from('ticket_price_tiers')
                                            .insert({
                                              event_id: event.id,
                                              pta_id: ptaId,
                                              name: 'New Tier',
                                              price: 0,
                                              display_order: ticketPriceTiers.length,
                                            });
                                          if (error) {
                                            toast.error("Failed to add price tier");
                                          } else {
                                            toast.success("Price tier added");
                                            const { data } = await supabase
                                              .from('ticket_price_tiers')
                                              .select('*')
                                              .eq('event_id', event.id)
                                              .order('display_order');
                                            setTicketPriceTiers(data || []);
                                          }
                                        }}
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Tier
                                      </Button>
                                    </div>
                                    
                                    {ticketPriceTiers.length === 0 ? (
                                      <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                                        <PoundSterling className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                        <p>No price tiers defined</p>
                                        <p className="text-xs mt-1">The default ticket price will be used</p>
                                      </div>
                                    ) : (
                                      <div className="space-y-3">
                                        {ticketPriceTiers.map((tier) => (
                                          <div key={tier.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                                            <div className="flex-1 grid grid-cols-2 gap-3">
                                              <Input 
                                                placeholder="Tier name (e.g., Adult)"
                                                defaultValue={tier.name}
                                                onBlur={async (e) => {
                                                  if (e.target.value !== tier.name) {
                                                    await supabase
                                                      .from('ticket_price_tiers')
                                                      .update({ name: e.target.value })
                                                      .eq('id', tier.id);
                                                  }
                                                }}
                                              />
                                              <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                                                <Input 
                                                  type="number"
                                                  step="0.01"
                                                  min="0"
                                                  placeholder="0.00"
                                                  className="pl-7"
                                                  defaultValue={tier.price}
                                                  onBlur={async (e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    if (newPrice !== tier.price) {
                                                      await supabase
                                                        .from('ticket_price_tiers')
                                                        .update({ price: newPrice })
                                                        .eq('id', tier.id);
                                                    }
                                                  }}
                                                />
                                              </div>
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={async () => {
                                                const { error } = await supabase
                                                  .from('ticket_price_tiers')
                                                  .delete()
                                                  .eq('id', tier.id);
                                                if (error) {
                                                  toast.error("Failed to delete tier");
                                                } else {
                                                  setTicketPriceTiers(ticketPriceTiers.filter(t => t.id !== tier.id));
                                                  toast.success("Tier deleted");
                                                }
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4 text-foreground" />
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </TabsContent>
                                  
                                  <TabsContent value="extras" className="space-y-4 mt-4">
                                    <div className="flex justify-between items-center">
                                      <p className="text-sm text-muted-foreground">Add-ons purchasable with tickets (e.g., Hot Dog, Drink)</p>
                                      <Button 
                                        size="sm"
                                        onClick={async () => {
                                          const { error } = await supabase
                                            .from('ticket_extras')
                                            .insert({
                                              event_id: event.id,
                                              pta_id: ptaId,
                                              name: 'New Extra',
                                              price: 0,
                                              display_order: ticketExtras.length,
                                            });
                                          if (error) {
                                            toast.error("Failed to add extra");
                                          } else {
                                            toast.success("Extra added");
                                            const { data } = await supabase
                                              .from('ticket_extras')
                                              .select('*')
                                              .eq('event_id', event.id)
                                              .order('display_order');
                                            setTicketExtras(data || []);
                                          }
                                        }}
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Extra
                                      </Button>
                                    </div>
                                    
                                    {ticketExtras.length === 0 ? (
                                      <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                                        <ShoppingBag className="h-10 w-10 mx-auto mb-2 opacity-30" />
                                        <p>No extras defined</p>
                                        <p className="text-xs mt-1">Add items that can be purchased with tickets</p>
                                      </div>
                                    ) : (
                                      <div className="space-y-3">
                                        {ticketExtras.map((extra) => (
                                          <div key={extra.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                                            <div className="flex-1 grid grid-cols-2 gap-3">
                                              <Input 
                                                placeholder="Extra name (e.g., Hot Dog)"
                                                defaultValue={extra.name}
                                                onBlur={async (e) => {
                                                  if (e.target.value !== extra.name) {
                                                    await supabase
                                                      .from('ticket_extras')
                                                      .update({ name: e.target.value })
                                                      .eq('id', extra.id);
                                                  }
                                                }}
                                              />
                                              <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                                                <Input 
                                                  type="number"
                                                  step="0.01"
                                                  min="0"
                                                  placeholder="0.00"
                                                  className="pl-7"
                                                  defaultValue={extra.price}
                                                  onBlur={async (e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    if (newPrice !== extra.price) {
                                                      await supabase
                                                        .from('ticket_extras')
                                                        .update({ price: newPrice })
                                                        .eq('id', extra.id);
                                                    }
                                                  }}
                                                />
                                              </div>
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={async () => {
                                                const { error } = await supabase
                                                  .from('ticket_extras')
                                                  .delete()
                                                  .eq('id', extra.id);
                                                if (error) {
                                                  toast.error("Failed to delete extra");
                                                } else {
                                                  setTicketExtras(ticketExtras.filter(e => e.id !== extra.id));
                                                  toast.success("Extra deleted");
                                                }
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4 text-foreground" />
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </TabsContent>
                                </Tabs>
                                
                                <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                                  <Button 
                                    variant="outline" 
                                    onClick={() => setTicketDialogOpenId(null)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button 
                                    onClick={() => {
                                      const form = document.getElementById(`ticket-settings-form-${event.id}`) as HTMLFormElement;
                                      if (form) {
                                        form.requestSubmit();
                                      } else {
                                        setTicketDialogOpenId(null);
                                      }
                                    }}
                                  >
                                    <Save className="h-4 w-4 mr-1" />
                                    Save Settings
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => navigate(`/event-checkin/${event.id}`)}
                            >
                              <ScanLine className="h-4 w-4 mr-1" />
                              Check-in
                            </Button>
                            <Popover>
                              <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="flex-1">
                                  <Users className="h-4 w-4 mr-1" />
                                  Attendees
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-48 p-2">
                                <div className="space-y-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start"
                                    onClick={async () => {
                                      try {
                                        // Fetch actual attendee data with extras
                                        const { data: purchases, error } = await supabase
                                          .from('ticket_purchases')
                                          .select(`
                                            id, buyer_name, buyer_email, buyer_phone, qr_code, total_amount, payment_status, created_at,
                                            ticket_purchase_items (
                                              id, attendee_name, checked_in, checked_in_at,
                                              ticket_price_tiers (name, price),
                                              ticket_purchase_item_extras (
                                                quantity,
                                                ticket_extras (name, price)
                                              )
                                            )
                                          `)
                                          .eq('event_id', event.id);
                                        
                                        if (error) {
                                          console.error('Error fetching purchases:', error);
                                          toast.error("Failed to fetch attendee data");
                                          return;
                                        }
                                        
                                        // Build CSV rows with extras
                                        const csvRows = [['Buyer Name', 'Buyer Email', 'Buyer Phone', 'QR Code', 'Ticket Type', 'Attendee Name', 'Extras', 'Ticket Price', 'Extras Total', 'Payment Status', 'Checked In', 'Check-in Time']];
                                        
                                        (purchases || []).forEach((purchase: any) => {
                                          (purchase.ticket_purchase_items || []).forEach((item: any) => {
                                            // Format extras
                                            const extras = (item.ticket_purchase_item_extras || [])
                                              .map((e: any) => `${e.ticket_extras?.name || 'Extra'} x${e.quantity}`)
                                              .join('; ');
                                            const extrasTotal = (item.ticket_purchase_item_extras || [])
                                              .reduce((sum: number, e: any) => sum + ((e.ticket_extras?.price || 0) * e.quantity), 0);
                                            
                                            csvRows.push([
                                              purchase.buyer_name,
                                              purchase.buyer_email,
                                              purchase.buyer_phone || '',
                                              purchase.qr_code,
                                              item.ticket_price_tiers?.name || 'Standard',
                                              item.attendee_name || '',
                                              extras || 'None',
                                              `£${(item.ticket_price_tiers?.price || 0).toFixed(2)}`,
                                              `£${extrasTotal.toFixed(2)}`,
                                              purchase.payment_status,
                                              item.checked_in ? 'Yes' : 'No',
                                              item.checked_in_at ? new Date(item.checked_in_at).toLocaleString() : ''
                                            ]);
                                          });
                                        });
                                        
                                        const csvContent = csvRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
                                        
                                        const blob = new Blob([csvContent], { type: 'text/csv' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `${event.name.replace(/[^a-z0-9]/gi, '_')}_attendees.csv`;
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        toast.success("CSV downloaded");
                                      } catch (err) {
                                        console.error('CSV export error:', err);
                                        toast.error("Failed to export CSV");
                                      }
                                    }}
                                  >
                                    <img src={csvIcon} alt="CSV" className="h-4 w-4 mr-2" />
                                    Download CSV
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start"
                                    onClick={async () => {
                                      try {
                                        // Fetch actual attendee data for PDF with extras
                                        const { data: purchases, error } = await supabase
                                          .from('ticket_purchases')
                                          .select(`
                                            id, buyer_name, buyer_email, qr_code, total_amount, payment_status, created_at,
                                            ticket_purchase_items (
                                              id, attendee_name, checked_in, checked_in_at,
                                              ticket_price_tiers (name, price),
                                              ticket_purchase_item_extras (
                                                quantity,
                                                ticket_extras (name, price)
                                              )
                                            )
                                          `)
                                          .eq('event_id', event.id);
                                        
                                        if (error) {
                                          console.error('Error fetching purchases:', error);
                                          toast.error("Failed to fetch attendee data");
                                          return;
                                        }
                                        
                                        const doc = new jsPDF();
                                        doc.setFontSize(18);
                                        doc.text(`Attendees: ${event.name}`, 14, 22);
                                        doc.setFontSize(11);
                                        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
                                        
                                        // Count stats
                                        let totalTickets = 0;
                                        let checkedIn = 0;
                                        let totalRevenue = 0;
                                        
                                        const tableData: string[][] = [];
                                        (purchases || []).forEach((purchase: any) => {
                                          totalRevenue += Number(purchase.total_amount) || 0;
                                          (purchase.ticket_purchase_items || []).forEach((item: any) => {
                                            totalTickets++;
                                            if (item.checked_in) checkedIn++;
                                            
                                            // Format extras for PDF
                                            const extras = (item.ticket_purchase_item_extras || [])
                                              .map((e: any) => `${e.ticket_extras?.name || 'Extra'} x${e.quantity}`)
                                              .join(', ');
                                            
                                            tableData.push([
                                              purchase.buyer_name,
                                              item.attendee_name || '-',
                                              item.ticket_price_tiers?.name || 'Standard',
                                              extras || '-',
                                              item.checked_in ? 'Yes' : 'No'
                                            ]);
                                          });
                                        });
                                        
                                        doc.text(`Total Tickets: ${totalTickets} | Checked In: ${checkedIn} | Revenue: £${totalRevenue.toFixed(2)}`, 14, 38);
                                        
                                        // Use autoTable
                                        autoTable(doc, {
                                          startY: 45,
                                          head: [['Buyer', 'Attendee', 'Ticket Type', 'Extras', 'Checked In']],
                                          body: tableData.length > 0 ? tableData : [['No attendees yet', '', '', '', '']],
                                          theme: 'striped',
                                          styles: { fontSize: 9 },
                                          headStyles: { fillColor: [99, 102, 241] },
                                        });
                                        
                                        doc.save(`${event.name.replace(/[^a-z0-9]/gi, '_')}_attendees.pdf`);
                                        toast.success("PDF downloaded");
                                      } catch (err) {
                                        console.error('PDF export error:', err);
                                        toast.error("Failed to export PDF");
                                      }
                                    }}
                                  >
                                    <img src={pdfIcon} alt="PDF" className="h-4 w-4 mr-2" />
                                    Download PDF
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>}

        {isStandardPlan && <TabsContent value="planner" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Event Planner</CardTitle>
              <CardDescription>Generate AI-powered event plans with resource summaries</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Event Selector */}
                <div className="space-y-2">
                  <Label>Select Event</Label>
                  <Select value={selectedPlannerEvent} onValueChange={setSelectedPlannerEvent}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an event to plan..." />
                    </SelectTrigger>
                    <SelectContent>
                      {events.filter(e => !e.is_archived).map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.name} - {new Date(event.event_date).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedPlannerEvent && (() => {
                  const event = events.find(e => e.id === selectedPlannerEvent);
                  if (!event) return null;
                  
                  const assignedStalls = stallAssignments[event.id] || [];
                  
                  // Calculate totals
                  let totalVolunteers = 0;
                  let totalFloats = 0;
                  const allEquipment: Record<string, number> = {};
                  
                  assignedStalls.forEach((stall: any) => {
                    totalVolunteers += stall.volunteers_required || 0;
                    totalFloats += stall.floats_needed || 0;
                    (stall.utilities || []).forEach((util: string) => {
                      const match = util.match(/^(.+?)\s*\((\d+)\)$/);
                      const name = match ? match[1] : util;
                      const qty = match ? parseInt(match[2]) : 1;
                      allEquipment[name] = (allEquipment[name] || 0) + qty;
                    });
                  });

                  return (
                    <div className="p-4 border border-border rounded-lg">
                      <h4 className="font-semibold mb-3">{event.name}</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        {new Date(event.event_date).toLocaleDateString()}
                        {event.time && ` • ${event.time}`}
                        {event.location && ` • ${event.location}`}
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-sm font-medium mb-2">PTA Stalls ({assignedStalls.length})</h5>
                          <ul className="text-sm space-y-1 text-muted-foreground">
                            {assignedStalls.length > 0 ? (
                              assignedStalls.slice(0, 5).map((stall: any, idx: number) => (
                                <li key={idx}>• {stall.name}</li>
                              ))
                            ) : (
                              <li>• No stalls assigned yet</li>
                            )}
                            {assignedStalls.length > 5 && (
                              <li className="text-primary">+ {assignedStalls.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                        
                        <div>
                          <h5 className="text-sm font-medium mb-2">Resources Required</h5>
                          <ul className="text-sm space-y-1 text-muted-foreground">
                            <li>• Volunteers: {totalVolunteers}</li>
                            <li>• Cash Floats: {totalFloats}</li>
                            <li>• Equipment: {Object.keys(allEquipment).length > 0 
                              ? Object.entries(allEquipment).slice(0, 3).map(([name, qty]) => `${name}${qty > 1 ? ` x ${qty}` : ''}`).join(', ')
                              : 'None'
                            }</li>
                          </ul>
                        </div>

                        <div>
                          <h5 className="text-sm font-medium mb-2">Catering</h5>
                          <div className="flex items-center gap-2">
                            {cateringCounts[event.id] > 0 ? (
                              <Badge className="bg-blue-100 text-blue-800">
                                {cateringCounts[event.id]} items assigned
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">No catering assigned</span>
                            )}
                          </div>
                        </div>

                        <div>
                          <h5 className="text-sm font-medium mb-2">External Vendors</h5>
                          <span className="text-sm text-muted-foreground">
                            {stallCounts[event.id] > 0 
                              ? `${stallCounts[event.id]} applications` 
                              : 'No applications'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm font-medium text-muted-foreground mb-2">Download Summary</p>
                        <div className="flex items-center gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="icon"
                                  onClick={() => downloadEventSummaryPDF(event)}
                                >
                                  <img src={pdfIcon} alt="PDF" className="h-5 w-5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Download Summary PDF</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="icon"
                                  onClick={() => downloadEventSummaryWord(event)}
                                >
                                  <img src={wordIcon} alt="Word" className="h-5 w-5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Download as Word (editable)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {!selectedPlannerEvent && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Select an event above to view planning details and generate a plan</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>}
      </Tabs>

      <Dialog open={applicationsDialogOpen} onOpenChange={setApplicationsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stall Applications - {selectedEvent?.name}</DialogTitle>
            <DialogDescription>
              {selectedEvent && new Date(selectedEvent.event_date).toLocaleDateString()} • {stallApplications.length} application{stallApplications.length !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {stallApplications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No applications yet for this event
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applied</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stallApplications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="font-medium">{app.business_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{app.contact_name}</div>
                          <div className="text-muted-foreground">{app.business_email}</div>
                          <div className="text-muted-foreground">{app.contact_phone}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-sm line-clamp-2">{app.business_description}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          app.status === 'approved' ? 'default' : 
                          app.status === 'declined' ? 'destructive' : 
                          'secondary'
                        }>
                          {app.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(app.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editEventDialogOpen} onOpenChange={setEditEventDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
            <DialogDescription>Update event details</DialogDescription>
          </DialogHeader>
          {editingEvent && (
            <form 
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const isTicketed = formData.get('edit-is-ticketed') === 'on';
                
                const { error } = await supabase
                  .from('events')
                  .update({
                    name: formData.get('event-name') as string,
                    event_date: formData.get('event-date') as string,
                    time: formData.get('event-time') as string,
                    location: formData.get('event-location') as string,
                    description: formData.get('event-description') as string,
                    is_ticketed: isTicketed,
                    max_tickets: isTicketed ? parseInt(formData.get('edit-max-tickets') as string) || null : null,
                    named_tickets_required: isTicketed && formData.get('edit-named-tickets') === 'on',
                    show_ticket_availability: formData.get('edit-show-ticket-availability') === 'on' || !isTicketed,
                    show_free_label: formData.get('edit-show-free-label') === 'on',
                  })
                  .eq('id', editingEvent.id);

                if (error) {
                  toast.error("Failed to update event");
                  console.error(error);
                } else {
                  toast.success("Event updated successfully");
                  setEditEventDialogOpen(false);
                  fetchEvents();
                }
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="edit-event-name">Event Name</Label>
                <Input 
                  id="edit-event-name" 
                  name="event-name"
                  defaultValue={editingEvent.name}
                  placeholder="e.g., Summer Fair 2025" 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-event-date">Date</Label>
                  <Input 
                    id="edit-event-date" 
                    name="event-date"
                    type="date"
                    defaultValue={editingEvent.event_date?.split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-event-time">Time</Label>
                  <Input 
                    id="edit-event-time" 
                    name="event-time"
                    type="time"
                    defaultValue={editingEvent.time}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-event-location">Location</Label>
                <Input 
                  id="edit-event-location" 
                  name="event-location"
                  defaultValue={editingEvent.location}
                  placeholder="e.g., School Field" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-event-description">Description</Label>
                <Textarea 
                  id="edit-event-description" 
                  name="event-description"
                  defaultValue={editingEvent.description}
                  placeholder="Event details..." 
                  rows={3} 
                />
              </div>
              
              {isStandardPlan && (
              <>
              {/* Ticketing Options */}
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="edit-is-ticketed">Ticketed Event</Label>
                    <p className="text-sm text-muted-foreground">Enable ticket sales for this event</p>
                  </div>
                  <Switch 
                    id="edit-is-ticketed" 
                    name="edit-is-ticketed"
                    defaultChecked={editingEvent.is_ticketed}
                    onCheckedChange={(checked) => {
                      const maxTicketsField = document.getElementById('edit-max-tickets-container');
                      const namedTicketsField = document.getElementById('edit-named-tickets-container');
                      const showToggles = document.getElementById('edit-show-toggles-container');
                      if (maxTicketsField && namedTicketsField) {
                        maxTicketsField.style.display = checked ? 'block' : 'none';
                        namedTicketsField.style.display = checked ? 'flex' : 'none';
                      }
                      if (showToggles) {
                        showToggles.style.display = checked ? 'block' : 'none';
                      }
                    }} 
                  />
                </div>
                
                <div 
                  id="edit-max-tickets-container" 
                  className="space-y-2" 
                  style={{ display: editingEvent.is_ticketed ? 'block' : 'none' }}
                >
                  <Label htmlFor="edit-max-tickets">Maximum Tickets Available</Label>
                  <Input 
                    id="edit-max-tickets" 
                    name="edit-max-tickets" 
                    type="number" 
                    min="1" 
                    placeholder="e.g., 100"
                    defaultValue={editingEvent.max_tickets || ''}
                  />
                </div>
              
                {/* Show/Hide Toggles for Edit */}
                <div className="space-y-3" style={{ display: editingEvent.is_ticketed ? 'block' : 'none' }} id="edit-show-toggles-container">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="edit-show-ticket-availability">Show Ticket Availability</Label>
                      <p className="text-sm text-muted-foreground">Display remaining tickets on fundraising page</p>
                    </div>
                    <Switch id="edit-show-ticket-availability" name="edit-show-ticket-availability" defaultChecked={editingEvent.show_ticket_availability !== false} />
                  </div>
                </div>
              
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="edit-show-free-label">Show "Free" Label</Label>
                    <p className="text-sm text-muted-foreground">Display "Free" badge on event card when no ticket price</p>
                  </div>
                  <Switch id="edit-show-free-label" name="edit-show-free-label" defaultChecked={editingEvent.show_free_label !== false} />
                </div>
                
                <div 
                  id="edit-named-tickets-container" 
                  className="items-center justify-between" 
                  style={{ display: editingEvent.is_ticketed ? 'flex' : 'none' }}
                >
                  <div className="space-y-0.5">
                    <Label htmlFor="edit-named-tickets">Named Tickets</Label>
                    <p className="text-sm text-muted-foreground">Require attendee names at purchase</p>
                  </div>
                  <Switch 
                    id="edit-named-tickets" 
                    name="edit-named-tickets"
                    defaultChecked={editingEvent.named_tickets_required}
                  />
                </div>
              </div>
              </>
              )}
              
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit">Update Event</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Event from Template Dialog */}
      <Dialog open={createFromTemplateOpen} onOpenChange={setCreateFromTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Event from Template</DialogTitle>
            <DialogDescription>
              {selectedTemplate && `Creating: ${selectedTemplate.name}`}
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <form 
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleCreateFromTemplate(formData);
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="template-event-name">Event Name</Label>
                <Input 
                  id="template-event-name" 
                  name="event-name"
                  defaultValue={selectedTemplate.name}
                  placeholder="Enter event name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Template Details</Label>
                <div className="p-3 bg-muted rounded-lg space-y-1 text-sm">
                  {selectedTemplate.description && (
                    <p className="text-muted-foreground">{selectedTemplate.description}</p>
                  )}
                  {selectedTemplate.location && (
                    <p className="text-muted-foreground">Location: {selectedTemplate.location}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template-event-date">Event Date</Label>
                  <Input 
                    id="template-event-date" 
                    name="event-date"
                    type="date"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-event-time">Event Time</Label>
                  <Input 
                    id="template-event-time" 
                    name="event-time"
                    type="time"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit">Create Event</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Stall to Event Dialog (from stalls tab - legacy) */}
      <Dialog open={assignStallDialogOpen} onOpenChange={setAssignStallDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Stall to Event</DialogTitle>
            <DialogDescription>
              {selectedStall && `Assigning: ${selectedStall.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Event</Label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {events.filter(e => !e.is_archived).length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No active events available
                  </p>
                ) : (
                  events.filter(e => !e.is_archived).map((event) => (
                    <div
                      key={event.id}
                      className="p-3 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => handleAssignStall(event.id)}
                    >
                      <h4 className="font-semibold">{event.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {new Date(event.event_date).toLocaleDateString()}
                        {event.time && ` • ${event.time}`}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Stall to Event Dialog (from events tab) */}
      <Dialog open={assignStallToEventDialogOpen} onOpenChange={(open) => {
        setAssignStallToEventDialogOpen(open);
        if (!open) {
          setSelectedStallIds([]);
          setSelectedEventForStallAssign(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Stalls to Event</DialogTitle>
            <DialogDescription>
              {selectedEventForStallAssign && `Event: ${selectedEventForStallAssign.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Stalls</Label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {reusableStalls.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No stalls available. Create stalls in the Stalls tab first.
                  </p>
                ) : (
                  reusableStalls.map((stall) => {
                    const isSelected = selectedStallIds.includes(stall.id);
                    const alreadyAssigned = selectedEventForStallAssign && stallAssignments[selectedEventForStallAssign.id]?.some((a: any) => a.stall_id === stall.id);
                    return (
                      <div
                        key={stall.id}
                        className={`p-3 border rounded-lg transition-colors ${alreadyAssigned ? 'opacity-50 cursor-not-allowed border-muted' : 'cursor-pointer hover:bg-accent border-border'} ${isSelected ? 'bg-accent border-primary' : ''}`}
                        onClick={() => {
                          if (alreadyAssigned) return;
                          setSelectedStallIds(prev =>
                            prev.includes(stall.id)
                              ? prev.filter(id => id !== stall.id)
                              : [...prev, stall.id]
                          );
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isSelected || !!alreadyAssigned}
                            disabled={!!alreadyAssigned}
                            onCheckedChange={() => {
                              if (alreadyAssigned) return;
                              setSelectedStallIds(prev =>
                                prev.includes(stall.id)
                                  ? prev.filter(id => id !== stall.id)
                                  : [...prev, stall.id]
                              );
                            }}
                          />
                          <div>
                            <h4 className="font-semibold">{stall.name}</h4>
                            {stall.description && (
                              <p className="text-sm text-muted-foreground">{stall.description}</p>
                            )}
                            {alreadyAssigned && (
                              <p className="text-xs text-muted-foreground">Already assigned</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={handleAssignStallsToEvent}
                disabled={selectedStallIds.length === 0}
              >
                Assign {selectedStallIds.length > 0 ? `(${selectedStallIds.length})` : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Catering Dialog */}
      <Dialog open={cateringDialogOpen} onOpenChange={setCateringDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5 text-primary" />
              Catering for {selectedEvent?.name}
            </DialogTitle>
            <DialogDescription>
              Catering items allocated to this event
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {eventCatering.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <UtensilsCrossed className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No catering assigned to this event yet.</p>
                <p className="text-sm mt-1">Use the Catering Dashboard to assign items.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventCatering.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {item.menu_item?.icon && (
                            <span className="text-lg">{item.menu_item.icon}</span>
                          )}
                          {item.stock_item?.item_name || item.menu_item?.name || 'Unknown Item'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.stock_item ? "secondary" : "default"}>
                          {item.stock_item ? "Stock" : "Menu"}
                        </Badge>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as Template</DialogTitle>
            <DialogDescription>
              {eventToSaveAsTemplate && `Saving "${eventToSaveAsTemplate.name}" as a reusable template`}
            </DialogDescription>
          </DialogHeader>
          {eventToSaveAsTemplate && (
            <form 
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleSaveAsTemplate(formData);
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="template-description">Template Details</Label>
                <Textarea 
                  id="template-description" 
                  name="template-description"
                  placeholder="Add notes about this template (e.g., typical setup, requirements, tips for next year)"
                  defaultValue={eventToSaveAsTemplate.description || ''}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  This will be shown when using this template to create new events.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit">Save Template</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Stall Dialog */}
      <Dialog open={editStallDialogOpen} onOpenChange={setEditStallDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stall</DialogTitle>
            <DialogDescription>Update the stall details</DialogDescription>
          </DialogHeader>
          {editingStall && (
            <form 
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                
                const utilitiesFormatted = editStallUtilities.map(u => {
                  const noQtyItems = ["Power", "Water", "Gas", "WiFi", "Lighting"];
                  if (noQtyItems.includes(u.name) || u.qty <= 1) {
                    return u.name;
                  }
                  return `${u.name} (${u.qty})`;
                });

                const { error } = await supabase
                  .from('reusable_stalls')
                  .update({
                    name: formData.get('edit-stall-name') as string,
                    volunteers_required: parseInt(formData.get('edit-stall-volunteers') as string) || 1,
                    floats_needed: parseInt(formData.get('edit-stall-float') as string) || 0,
                    utilities: utilitiesFormatted,
                    price_tiers: editPriceTiers.filter(t => t.description && t.price > 0),
                  })
                  .eq('id', editingStall.id);

                if (error) {
                  toast.error("Failed to update stall");
                } else {
                  toast.success("Stall updated successfully");
                  fetchReusableStalls();
                  setEditStallDialogOpen(false);
                  setEditingStall(null);
                  setEditStallUtilities([]);
                  setEditPriceTiers([]);
                }
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="edit-stall-name">Stall Name</Label>
                <Input id="edit-stall-name" name="edit-stall-name" defaultValue={editingStall.name} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-stall-volunteers">Volunteers Required</Label>
                <Input id="edit-stall-volunteers" name="edit-stall-volunteers" type="number" defaultValue={editingStall.volunteers_required} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-stall-float">Number of Floats Needed</Label>
                <Input id="edit-stall-float" name="edit-stall-float" type="number" defaultValue={editingStall.floats_needed} />
              </div>
              <div className="space-y-2">
                <Label>Utilities and Equipment</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start h-auto min-h-10 py-2">
                      {editStallUtilities.length === 0 ? (
                        <span className="text-muted-foreground">Select utilities...</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {editStallUtilities.map((utility) => (
                            <Badge key={utility.name} variant="secondary" className="mr-1">
                              {utility.name} {utility.qty > 1 && `(${utility.qty})`}
                              <span
                                className="ml-1 hover:text-destructive cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditStallUtilities(editStallUtilities.filter(u => u.name !== utility.name));
                                }}
                              >
                                <X className="h-3 w-3" />
                              </span>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      {availableUtilities.map((utility) => {
                        const isSelected = editStallUtilities.some(u => u.name === utility);
                        const noQtyItems = ["Power", "Water", "Gas", "WiFi", "Lighting"];
                        return (
                          <div key={utility} className="flex items-center gap-2">
                            <Checkbox
                              id={`edit-utility-${utility}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setEditStallUtilities([...editStallUtilities, { name: utility, qty: 1 }]);
                                } else {
                                  setEditStallUtilities(editStallUtilities.filter(u => u.name !== utility));
                                }
                              }}
                            />
                            <Label htmlFor={`edit-utility-${utility}`} className="flex-1">{utility}</Label>
                            {isSelected && !noQtyItems.includes(utility) && (
                              <Input
                                type="number"
                                min="1"
                                className="w-16 h-7"
                                value={editStallUtilities.find(u => u.name === utility)?.qty || 1}
                                onChange={(e) => {
                                  const qty = parseInt(e.target.value) || 1;
                                  setEditStallUtilities(editStallUtilities.map(u => 
                                    u.name === utility ? { ...u, qty } : u
                                  ));
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Pricing (Optional)</Label>
                <div className="space-y-2">
                  {editPriceTiers.map((tier, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        placeholder="Description (e.g., 1 go)"
                        value={tier.description}
                        onChange={(e) => {
                          const newTiers = [...editPriceTiers];
                          newTiers[index].description = e.target.value;
                          setEditPriceTiers(newTiers);
                        }}
                        className="flex-1"
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={tier.price || ''}
                          onChange={(e) => {
                            const newTiers = [...editPriceTiers];
                            newTiers[index].price = parseFloat(e.target.value) || 0;
                            setEditPriceTiers(newTiers);
                          }}
                          className="w-20"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditPriceTiers(editPriceTiers.filter((_, i) => i !== index))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditPriceTiers([...editPriceTiers, { description: '', price: 0 }])}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Price Tier
                  </Button>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
